import { useCallback } from 'react';
import useConfig from 'hooks/useConfig';
import { I18n } from 'types/config';

type Vars = Record<string, string | number>;
type Entry = string | ((vars: Vars) => string);
type Dict = Record<string, Entry>;

const en: Dict = {
  'install.cardTitle': 'Install Accountly',
  'install.cardSub': 'Opens faster and works offline',
  'install.action': 'Install',
  'install.samsungTitle': 'Install with Chrome',
  'install.samsungStep1': 'Copy this link and open it in Chrome.',
  'install.samsungStep2': 'In Chrome, tap the ⋮ menu at the top right.',
  'install.samsungStep3': 'Tap "Install app" and confirm.',
  'install.copyLink': 'Copy link',
  'install.copied': 'Copied',
  'install.button': 'Install app',
  'install.desc': 'Add Accountly to your home screen',
  'install.iosTitle': 'Add to Home Screen',
  'install.step1': 'Tap the Share button at the bottom of your browser.',
  'install.step2': 'Scroll down and tap "Add to Home Screen".',
  'install.step3': 'Tap "Add". Accountly now opens like an app.',
  'install.gotIt': 'Got it',
  'notifications.title': 'Notifications',
  'notifications.emptyTitle': 'You\'re all caught up',
  'notifications.emptySub': 'Updates about your payments and account will show up here.',
  'due.add': 'Due date',
  'due.setDate': 'Set due date',
  'due.title': 'Due',
  'due.today': 'Due today',
  'due.tomorrowLabel': 'Due tomorrow',
  'due.inDays': (v) => `Due in ${v.days} days`,
  'due.overdueBy': (v) => v.days === 1 ? 'Overdue by 1 day' : `Overdue by ${v.days} days`,
  'due.failed': 'Could not update the due date',
  'common.save': 'Save',
  'common.cancel': 'Cancel',
  'common.close': 'Close',
  'common.delete': 'Delete',
  'common.edit': 'Edit',
  'common.add': 'Add',
  'common.search': 'Search',
  'common.seeAll': 'See all',
  'common.saving': 'Saving…',
  'common.name': 'Name',
  'common.phone': 'Phone',
  'common.balance': 'Balance',
  'common.details': 'Details',
  'common.settings': 'Settings',
  'common.apply': 'Apply',
  'common.reset': 'Reset',

  'nav.home': 'Home',
  'nav.contacts': 'Contacts',
  'nav.payments': 'Transactions',
  'nav.more': 'More',

  'header.manageKhata': 'Manage khata',
  'role.owner': 'Owner',
  'role.admin': 'Admin',
  'role.staff': 'Staff',
  'role.super_admin': 'Super Admin',

  'menu.profile': 'Profile',
  'menu.settings': 'Settings',
  'menu.logout': 'Logout',

  'home.youllGet': "You'll Get",
  'home.youllGive': "You'll Give",
  'home.viewFullReport': 'View full report',
  'home.addContact': 'Add Contact',
  'home.newContact': 'New contact',
  'home.newEntry': 'New Transaction',
  'home.recordPayment': 'Record a payment',
  'home.contacts': 'Recent Contacts',
  'home.recentPayments': 'Recent Transactions',
  'home.noContactsYet': 'No contacts yet',
  'home.noContactsSub': 'Add your first contact to start tracking who owes what.',
  'home.addContactCta': 'Add contact',
  'home.noPaymentsYet': 'No payments yet',
  'home.noPaymentsSub': 'Open a contact and record a Send or Receive to see it here.',
  'home.paidTo': (v) => `You gave to ${v.name}`,
  'home.receivedFrom': (v) => `You got from ${v.name}`,

  'contacts.title': 'Contacts',
  'contactType.title': 'Type',
  'contactType.customer': 'Customer',
  'contactType.supplier': 'Supplier',
  'contactType.business': 'Business',
  'contactType.friend': 'Friend',
  'contactType.family': 'Family',
  'contacts.searchPlaceholder': 'Search by name or phone',
  'contacts.noMatches': 'No matches',
  'contacts.tryDifferent': 'Try a different name or number.',
  'contacts.tapAddToCreate': 'Tap Add to create your first contact.',

  'detail.youWillGet': 'You Will Get',
  'detail.youWillGive': 'You Will Give',
  'detail.transactions': 'Transactions',
  'detail.recentTransactions': 'Recent Transactions',
  'detail.youGave': 'You Gave',
  'detail.youGot': 'You Got',
  'detail.noTransactionsYet': 'No transactions yet',
  'detail.recordBelow': 'Record a Send or Receive below.',
  'detail.shareLedger': 'Share Ledger',
  'detail.failedGenerateLedger': 'Failed to generate ledger',
  'detail.youGaveBtn': 'You Gave',
  'detail.youGotBtn': 'You Got',
  'detail.sendReminderWhatsapp': 'WhatsApp Reminder',
  'detail.sendReminderSms': 'SMS Reminder',
  'detail.reminderDue': (v) =>
    `Hi ${v.name}, this is a reminder that you have a pending payment of ${v.amount} to ${v.business}. Please pay at your earliest convenience. Thank you!`,
  'detail.reminderOwed': (v) => `Hi ${v.name}, we owe you ${v.amount} from ${v.business}. We will settle this soon. Thank you!`,

  'contactSettings.title': 'Contact Settings',
  'contactSettings.notFound': 'Contact not found.',
  'contactSettings.actions': 'Actions',
  'contactSettings.editContact': 'Edit Contact',
  'contactSettings.editContactSub': 'Update name, phone or address',
  'contactSettings.deleteContact': 'Delete Contact',
  'contactSettings.deleteContactSub': 'Permanently remove this contact',
  'contactSettings.deleteQ': 'Delete contact?',
  'contactSettings.deleteBody': (v) => `${v.name} and their transaction history will be removed. This cannot be undone.`,
  'contactSettings.failedDelete': 'Failed to delete contact',

  'contactForm.contactName': 'Contact Name',
  'contactForm.enterName': 'Enter name',
  'contactForm.nameRequired': 'Name is required',
  'contactForm.phoneNumber': 'Phone Number',
  'contactForm.enterPhoneNumber': 'Enter phone number',
  'contactForm.invalidPhone': 'Enter a valid phone number',
  'contactForm.address': 'Address',
  'contactForm.optional': 'Optional',
  'contactForm.addTitle': 'Add Contact',
  'contactForm.editTitle': 'Edit Contact',
  'contactForm.saveChanges': 'Save Changes',
  'contactForm.businessInfoNotFound': 'Business information not found',
  'contactForm.failedAdd': 'Failed to add contact',
  'contactForm.failedUpdate': 'Failed to update contact',
  'contactForm.failedImageUpload': 'Failed to upload photo',
  'contactForm.alreadyExists': 'Contact already exists with this phone number',
  'contactForm.selectCountry': 'Select Country',
  'contactForm.searchCountries': 'Search countries',

  'payment.editEntry': 'Edit Transaction',
  'payment.youGaveTitle': 'You Gave',
  'payment.youGotTitle': 'You Got',
  'payment.to': 'to',
  'payment.from': 'from',
  'payment.addNote': 'Add a note (optional)',
  'payment.update': 'Update',
  'payment.saveEntry': 'Save Transaction',
  'payment.entrySaved': 'Transaction saved',
  'payment.contactNotFound': 'Contact not found',
  'payment.failedUpdateEntry': 'Failed to update transaction',
  'payment.failedRecordEntry': 'Failed to record transaction',
  'payment.failedDeleteEntry': 'Failed to delete transaction',
  'payment.deleteEntryQ': 'Delete this transaction?',
  'payment.deleteEntryBody': 'This will update the contact balance. It cannot be undone.',
  'payment.date': 'Date',
  'payment.attachBills': 'Attach image',
  'payment.imageAttached': 'Image attached',
  'payment.failedAttachImage': 'Failed to attach image',
  'payment.comingSoon': 'Bill attachments are coming soon',
  'payment.tapToUse': 'tap to use',
  'transactionDetail.title': 'Transaction Details',
  'transactionDetail.note': 'Note',
  'transactionDetail.attachment': 'Attachment',
  'transactionDetail.viewAttachment': 'View attachment',

  'transactions.title': 'Transactions',
  'transactions.searchPayments': 'Search payments',
  'transactions.openContactRecord': 'Open a contact and record a Send or Receive.',
  'transactions.filters': 'Filters',
  'transactions.dateRangeLabel': 'Date Range',
  'transactions.typeLabel': 'Type',
  'transactions.filterToday': 'Today',
  'transactions.filterYesterday': 'Yesterday',
  'transactions.filterThisWeek': 'This Week',
  'transactions.filterThisMonth': 'This Month',
  'transactions.filterLastMonth': 'Last Month',
  'transactions.filterAllTime': 'All Time',
  'transactions.filterCustomRange': 'Custom Range',
  'transactions.filterAllTypes': 'All',
  'transactions.startDate': 'Start Date',
  'transactions.endDate': 'End Date',

  'expenses.title': 'Personal Expenses',
  'expenses.searchExpenses': 'Search expenses',
  'expenses.noExpensesYet': 'No expenses yet',
  'expenses.noExpensesSub': 'Tap Add to log your first personal expense.',
  'expenses.filters': 'Filters',
  'expenses.categoryLabel': 'Category',
  'expenses.totalSpent': 'Total spent',
  'expenses.moreThan': (v) => `${v.amount} more than ${v.period}`,
  'expenses.lessThan': (v) => `${v.amount} less than ${v.period}`,
  'expenses.sameAs': (v) => `Same as ${v.period}`,
  'expenses.cmpYesterday': 'yesterday',
  'expenses.cmpDayBefore': 'the day before',
  'expenses.cmpLastWeek': 'same time last week',
  'expenses.cmpLastMonth': 'same time last month',
  'expenses.cmpMonthBefore': 'the month before',
  'expenses.cmpPreviousDays': (v) => `the previous ${v.days} days`,

  'expense.category.food': 'Food',
  'expense.category.travel': 'Travel',
  'expense.category.rent': 'Rent',
  'expense.category.utilities': 'Utilities',
  'expense.category.shopping': 'Shopping',
  'expense.category.other': 'Other',

  'expense.newEntry': 'New Expense',
  'expense.editEntry': 'Edit Expense',
  'expense.addNote': 'Add a note (optional)',
  'expense.saveEntry': 'Save Expense',
  'expense.update': 'Update',
  'expense.failedRecordEntry': 'Failed to record expense',
  'expense.failedUpdateEntry': 'Failed to update expense',
  'expense.failedDeleteEntry': 'Failed to delete expense',
  'expense.deleteEntryQ': 'Delete this expense?',
  'expense.deleteEntryBody': 'This cannot be undone.',

  'expenseDetail.title': 'Expense Details',
  'expenseDetail.note': 'Note',

  'more.title': 'More',
  'more.profileDesc': 'Your name and phone',
  'more.settingsDesc': 'Theme and language',
  'more.personalExpenses': 'Personal Expenses',
  'more.personalExpensesDesc': 'Track your own daily spending',
  'more.reports': 'Reports',
  'more.reportsDesc': 'View spending and business insights',
  'more.notes': 'Notes',
  'more.notesDesc': 'Jot down quick notes',
  'more.reminders': 'Reminders',
  'more.remindersDesc': 'Get reminded at the right time',

  'notes.title': 'Notes',
  'notes.searchNotes': 'Search notes',
  'notes.noNotesYet': 'No notes yet',
  'notes.noNotesSub': 'Tap Add to write your first note.',

  'note.placeholder': 'Start typing...',
  'note.saving': 'Saving…',
  'note.saved': 'Saved',
  'note.deleteNoteQ': 'Delete this note?',
  'note.deleteNoteBody': 'This cannot be undone.',
  'note.failedSave': 'Failed to save note',
  'note.failedDelete': 'Failed to delete note',

  'reminders.title': 'Reminders',
  'reminders.new': 'New reminder',
  'reminders.reminder': 'Reminder',
  'reminders.upcoming': 'Upcoming',
  'reminders.past': 'Past',
  'reminders.today': 'Today',
  'reminders.tomorrow': 'Tomorrow',
  'reminders.yesterday': 'Yesterday',
  'reminders.later': 'Later',
  'reminders.emptyUpcoming': 'No reminders yet',
  'reminders.emptyUpcomingSub': "Tap Add and we'll remind you at the right time.",
  'reminders.emptyPast': 'Nothing here yet',
  'reminders.emptyPastSub': 'Reminders that have gone off will show up here.',
  'reminders.titlePlaceholder': 'What should we remind you about?',
  'reminders.notesPlaceholder': 'Add details (optional)',
  'reminders.when': 'When',
  'reminders.repeat': 'Repeat',
  'reminders.repeat.none': 'Once',
  'reminders.repeat.daily': 'Daily',
  'reminders.repeat.weekly': 'Weekly',
  'reminders.repeat.monthly': 'Monthly',
  'reminders.early': 'Early reminder',
  'reminders.early.none': 'None',
  'reminders.early.5': '5 min',
  'reminders.early.15': '15 min',
  'reminders.early.30': '30 min',
  'reminders.early.60': '1 hour',
  'reminders.early.1440': '1 day',
  'reminders.time': 'Time',
  'reminders.date': 'Date',
  'reminders.save': 'Save reminder',
  'reminders.remindedAt': (v) => `Reminded you · ${v.when}`,
  'reminders.snooze': 'Snooze',
  'reminders.snooze.10': '10 min',
  'reminders.snooze.60': '1 hour',
  'reminders.snooze.1440': 'Tomorrow',
  'reminders.done': 'Done',
  'reminders.markDone': 'Mark as done',
  'reminders.markDoneSub': 'Move it to Past without waiting',
  'reminders.state.fired': 'Reminded',
  'reminders.state.done': 'Done',
  'reminders.deleteQ': 'Delete this reminder?',
  'reminders.deleteBody': 'This cannot be undone.',
  'reminders.failedSave': 'Could not save the reminder',
  'reminders.failedUpdate': 'Could not update the reminder',
  'reminders.failedDelete': 'Could not delete the reminder',
  'reminders.notFound': 'This reminder no longer exists.',
  'reminders.needTitle': 'Add what you want to be reminded about.',
  'push.title': 'Reminder alerts',
  'push.offBody': 'Turn on notifications so reminders reach you even when Accountly is closed.',
  'push.onBody': 'Reminders will alert you on this device.',
  'push.installBody': 'On iPhone, add Accountly to your Home Screen to get reminder alerts.',
  'push.deniedBody': 'Notifications are blocked. Allow them for this site in your browser settings.',
  'push.unsupportedBody': "This browser can't show notifications.",
  'push.failed': 'Could not turn on notifications. Please try again.',

  'profile.title': 'Profile',
  'profile.businessName': 'Business Name',
  'profile.updated': 'Profile updated',
  'profile.failedUpdate': 'Failed to update profile',
  'profile.failedAvatarUpload': 'Failed to upload photo',
  'profile.failedLogoUpload': 'Failed to upload logo',
  'profile.businessLogo': 'Business Logo',
  'profile.businessLogoSub': 'Shown on invoices and shared ledgers',

  'settings.title': 'Settings',
  'settings.preferences': 'Preferences',
  'settings.darkMode': 'Dark mode',
  'settings.darkModeSub': 'Use a dark colour theme',
  'settings.language': 'Language',
  'settings.currency': 'Currency',
  'settings.themeSaveFail': 'Could not save theme preference',
  'settings.languageSaveFail': 'Could not save language preference',
  'settings.currencySaveFail': 'Could not save currency. You may not have permission to change it.',

  'reports.title': 'Reports',
  'reports.filters': 'Filters',
  'reports.collected': 'Collected',
  'reports.given': 'Given',
  'reports.net': 'Net',
  'reports.trend': 'Collected vs Given',
  'reports.topContacts': 'Top Contacts',
  'reports.noDataYet': 'No transactions in this range',
  'reports.noDataSub': 'Try a different date range.',

  'ledger.statementTitle': 'Statement of Account',
  'ledger.generatedOn': 'Generated on',
  'ledger.currentBalance': 'Current Balance',
  'ledger.date': 'Date',
  'ledger.description': 'Description',
  'ledger.debit': 'Debit',
  'ledger.credit': 'Credit',
  'ledger.balance': 'Balance',
  'ledger.footer': 'Generated via Accountly',
  'ledger.billTo': 'Bill To',
  'ledger.page': 'Page',
  'ledger.of': 'of',
  'ledger.amountDue': 'Amount Due',
  'ledger.creditBalance': 'Credit Balance',
  'ledger.givenTo': (v) => `Given to ${v.name}`,
  'ledger.receivedFrom': (v) => `Received from ${v.name}`,

  'auth.welcomeBack': 'Welcome back',
  'auth.signInToKhata': 'Sign in to your khata',
  'auth.enterPhoneNumber': 'Enter phone number',
  'auth.password': 'Password',
  'auth.signIn': 'Sign In',
  'auth.newToAccountly': 'New to Accountly?',
  'auth.createAccount': 'Create account',
  'auth.loginFailed': 'Login failed. Please try again.',
  'auth.createYourAccount': 'Create your account',
  'auth.startManaging': 'Start managing your khata in minutes',
  'auth.businessName': 'Business name',
  'auth.yourName': 'Your name',
  'auth.createAccountBtn': 'Create Account',
  'auth.creatingAccount': 'Creating account…',
  'auth.signingIn': 'Signing in…',
  'auth.alreadyHaveAccount': 'Already have an account?',
  'auth.signInLink': 'Sign in',
  'auth.registrationFailed': 'Registration failed. Please try again.'
};

const hi: Dict = {
  'install.cardTitle': 'Accountly इंस्टॉल करें',
  'install.cardSub': 'तेज़ खुलता है और ऑफ़लाइन भी चलता है',
  'install.action': 'इंस्टॉल',
  'install.samsungTitle': 'Chrome से इंस्टॉल करें',
  'install.samsungStep1': 'इस लिंक को कॉपी करके Chrome में खोलें।',
  'install.samsungStep2': 'Chrome में ऊपर दाईं ओर ⋮ मेन्यू दबाएं।',
  'install.samsungStep3': '"Install app" दबाएं और पुष्टि करें।',
  'install.copyLink': 'लिंक कॉपी करें',
  'install.copied': 'कॉपी हो गया',
  'install.button': 'ऐप इंस्टॉल करें',
  'install.desc': 'Accountly को अपनी होम स्क्रीन पर जोड़ें',
  'install.iosTitle': 'होम स्क्रीन पर जोड़ें',
  'install.step1': 'अपने ब्राउज़र के नीचे Share बटन दबाएं।',
  'install.step2': 'नीचे स्क्रॉल करें और "Add to Home Screen" दबाएं।',
  'install.step3': '"Add" दबाएं। अब Accountly ऐप की तरह खुलेगा।',
  'install.gotIt': 'समझ गया',
  'notifications.title': 'सूचनाएं',
  'notifications.emptyTitle': 'सब कुछ अपडेट है',
  'notifications.emptySub': 'आपके भुगतान और खाते से जुड़े अपडेट यहाँ दिखेंगे।',
  'due.add': 'तारीख़',
  'due.setDate': 'तारीख तय करें',
  'due.title': 'बकाया',
  'due.today': 'आज देय',
  'due.tomorrowLabel': 'कल देय',
  'due.inDays': (v) => `${v.days} दिन में देय`,
  'due.overdueBy': (v) => `${v.days} दिन की देरी`,
  'due.failed': 'तारीख अपडेट नहीं हो सकी',
  'common.save': 'सेव करें',
  'common.cancel': 'रद्द करें',
  'common.close': 'बंद करें',
  'common.delete': 'हटाएं',
  'common.edit': 'बदलें',
  'common.add': 'जोड़ें',
  'common.search': 'खोजें',
  'common.seeAll': 'सभी देखें',
  'common.saving': 'सेव हो रहा है…',
  'common.name': 'नाम',
  'common.phone': 'फ़ोन',
  'common.balance': 'बकाया',
  'common.details': 'विवरण',
  'common.settings': 'सेटिंग्स',
  'common.apply': 'लागू करें',
  'common.reset': 'रीसेट करें',

  'nav.home': 'होम',
  'nav.contacts': 'संपर्क',
  'nav.payments': 'लेन-देन',
  'nav.more': 'अधिक',

  'header.manageKhata': 'खाता प्रबंधित करें',
  'role.owner': 'मालिक',
  'role.admin': 'एडमिन',
  'role.staff': 'कर्मचारी',
  'role.super_admin': 'सुपर एडमिन',

  'menu.profile': 'प्रोफ़ाइल',
  'menu.settings': 'सेटिंग्स',
  'menu.logout': 'लॉगआउट',

  'home.youllGet': 'आपको मिलेंगे',
  'home.youllGive': 'आपको देने हैं',
  'home.viewFullReport': 'पूरी रिपोर्ट देखें',
  'home.addContact': 'संपर्क जोड़ें',
  'home.newContact': 'नया संपर्क',
  'home.newEntry': 'नया लेन-देन',
  'home.recordPayment': 'भुगतान दर्ज करें',
  'home.contacts': 'हाल के संपर्क',
  'home.recentPayments': 'हाल के लेन-देन',
  'home.noContactsYet': 'अभी कोई संपर्क नहीं',
  'home.noContactsSub': 'कौन कितना देता है, यह जानने के लिए अपना पहला संपर्क जोड़ें।',
  'home.addContactCta': 'संपर्क जोड़ें',
  'home.noPaymentsYet': 'अभी कोई भुगतान नहीं',
  'home.noPaymentsSub': 'किसी संपर्क को खोलें और "दिए" या "लिए" दर्ज करें, यह यहां दिखेगा।',
  'home.paidTo': (v) => `${v.name} को उधार दिए`,
  'home.receivedFrom': (v) => `${v.name} से उधार लिए`,

  'contacts.title': 'संपर्क',
  'contactType.title': 'प्रकार',
  'contactType.customer': 'ग्राहक',
  'contactType.supplier': 'सप्लायर',
  'contactType.business': 'बिज़नेस',
  'contactType.friend': 'दोस्त',
  'contactType.family': 'परिवार',
  'contacts.searchPlaceholder': 'नाम या फ़ोन से खोजें',
  'contacts.noMatches': 'कुछ नहीं मिला',
  'contacts.tryDifferent': 'अलग नाम या नंबर आज़माएं।',
  'contacts.tapAddToCreate': 'पहला संपर्क बनाने के लिए "जोड़ें" दबाएं।',

  'detail.youWillGet': 'आपको मिलेंगे',
  'detail.youWillGive': 'आपको देने हैं',
  'detail.transactions': 'लेन-देन',
  'detail.recentTransactions': 'हाल के लेन-देन',
  'detail.youGave': 'उधार दिए',
  'detail.youGot': 'उधार लिए',
  'detail.noTransactionsYet': 'अभी कोई लेन-देन नहीं',
  'detail.recordBelow': 'नीचे दिए या लिए दर्ज करें।',
  'detail.shareLedger': 'खाता शेयर करें',
  'detail.failedGenerateLedger': 'खाता बनाने में विफल',
  'detail.youGaveBtn': 'उधार दिए',
  'detail.youGotBtn': 'उधार लिए',
  'detail.sendReminderWhatsapp': 'व्हाट्सएप रिमाइंडर',
  'detail.sendReminderSms': 'एसएमएस रिमाइंडर',
  'detail.reminderDue': (v) => `नमस्ते ${v.name}, आपका ${v.business} को ${v.amount} का भुगतान बाकी है। कृपया जल्द भुगतान करें। धन्यवाद!`,
  'detail.reminderOwed': (v) => `नमस्ते ${v.name}, ${v.business} की तरफ से आपको ${v.amount} देने हैं। हम जल्द ही यह चुका देंगे। धन्यवाद!`,

  'contactSettings.title': 'संपर्क सेटिंग्स',
  'contactSettings.notFound': 'संपर्क नहीं मिला।',
  'contactSettings.actions': 'कार्रवाई',
  'contactSettings.editContact': 'संपर्क बदलें',
  'contactSettings.editContactSub': 'नाम, फ़ोन या पता बदलें',
  'contactSettings.deleteContact': 'संपर्क हटाएं',
  'contactSettings.deleteContactSub': 'इस संपर्क को हमेशा के लिए हटाएं',
  'contactSettings.deleteQ': 'संपर्क हटाएं?',
  'contactSettings.deleteBody': (v) => `${v.name} और उनका लेन-देन इतिहास हटा दिया जाएगा। यह वापस नहीं होगा।`,
  'contactSettings.failedDelete': 'संपर्क हटाने में विफल',

  'contactForm.contactName': 'संपर्क का नाम',
  'contactForm.enterName': 'नाम लिखें',
  'contactForm.nameRequired': 'नाम ज़रूरी है',
  'contactForm.phoneNumber': 'फ़ोन नंबर',
  'contactForm.enterPhoneNumber': 'फ़ोन नंबर डालें',
  'contactForm.invalidPhone': 'सही फ़ोन नंबर डालें',
  'contactForm.address': 'पता',
  'contactForm.optional': 'वैकल्पिक',
  'contactForm.addTitle': 'संपर्क जोड़ें',
  'contactForm.editTitle': 'संपर्क बदलें',
  'contactForm.saveChanges': 'बदलाव सेव करें',
  'contactForm.businessInfoNotFound': 'व्यवसाय की जानकारी नहीं मिली',
  'contactForm.failedAdd': 'संपर्क जोड़ने में विफल',
  'contactForm.failedUpdate': 'संपर्क बदलने में विफल',
  'contactForm.failedImageUpload': 'फ़ोटो अपलोड नहीं हो सकी',
  'contactForm.alreadyExists': 'इस फ़ोन नंबर से संपर्क पहले से मौजूद है',
  'contactForm.selectCountry': 'देश चुनें',
  'contactForm.searchCountries': 'देश खोजें',

  'payment.editEntry': 'लेन-देन बदलें',
  'payment.youGaveTitle': 'उधार दिए',
  'payment.youGotTitle': 'उधार लिए',
  'payment.to': 'को',
  'payment.from': 'से',
  'payment.addNote': 'नोट जोड़ें (वैकल्पिक)',
  'payment.update': 'अपडेट करें',
  'payment.saveEntry': 'लेन-देन सेव करें',
  'payment.entrySaved': 'लेन-देन सेव हो गया',
  'payment.contactNotFound': 'संपर्क नहीं मिला',
  'payment.failedUpdateEntry': 'लेन-देन अपडेट करने में विफल',
  'payment.failedRecordEntry': 'लेन-देन दर्ज करने में विफल',
  'payment.failedDeleteEntry': 'लेन-देन हटाने में विफल',
  'payment.deleteEntryQ': 'यह लेन-देन हटाएं?',
  'payment.deleteEntryBody': 'इससे संपर्क का बकाया बदल जाएगा। यह वापस नहीं होगा।',
  'payment.date': 'तारीख़',
  'payment.attachBills': 'फोटो जोड़ें',
  'payment.imageAttached': 'फोटो जुड़ गई',
  'payment.failedAttachImage': 'फोटो जोड़ने में विफल',
  'payment.comingSoon': 'बिल जोड़ना जल्द आ रहा है',
  'payment.tapToUse': 'उपयोग करने के लिए टैप करें',
  'transactionDetail.title': 'लेन-देन का विवरण',
  'transactionDetail.note': 'नोट',
  'transactionDetail.attachment': 'अटैचमेंट',
  'transactionDetail.viewAttachment': 'अटैचमेंट देखें',

  'transactions.title': 'लेन-देन',
  'transactions.searchPayments': 'भुगतान खोजें',
  'transactions.openContactRecord': 'किसी संपर्क को खोलें और दिए या लिए दर्ज करें।',
  'transactions.filters': 'फ़िल्टर',
  'transactions.dateRangeLabel': 'तारीख़ सीमा',
  'transactions.typeLabel': 'प्रकार',
  'transactions.filterToday': 'आज',
  'transactions.filterYesterday': 'कल',
  'transactions.filterThisWeek': 'इस हफ़्ते',
  'transactions.filterThisMonth': 'इस महीने',
  'transactions.filterLastMonth': 'पिछला महीना',
  'transactions.filterAllTime': 'सभी समय',
  'transactions.filterCustomRange': 'कस्टम रेंज',
  'transactions.filterAllTypes': 'सभी',
  'transactions.startDate': 'शुरुआत की तारीख़',
  'transactions.endDate': 'अंतिम तारीख़',

  'expenses.title': 'व्यक्तिगत खर्च',
  'expenses.searchExpenses': 'खर्च खोजें',
  'expenses.noExpensesYet': 'अभी कोई खर्च नहीं',
  'expenses.noExpensesSub': 'अपना पहला व्यक्तिगत खर्च जोड़ने के लिए जोड़ें दबाएं।',
  'expenses.filters': 'फ़िल्टर',
  'expenses.categoryLabel': 'श्रेणी',
  'expenses.totalSpent': 'कुल खर्च',
  'expenses.moreThan': (v) => `${v.period} से ${v.amount} ज़्यादा`,
  'expenses.lessThan': (v) => `${v.period} से ${v.amount} कम`,
  'expenses.sameAs': (v) => `${v.period} जितना ही`,
  'expenses.cmpYesterday': 'कल',
  'expenses.cmpDayBefore': 'परसों',
  'expenses.cmpLastWeek': 'पिछले हफ़्ते इसी समय',
  'expenses.cmpLastMonth': 'पिछले महीने इसी समय',
  'expenses.cmpMonthBefore': 'उससे पिछला महीना',
  'expenses.cmpPreviousDays': (v) => `पिछले ${v.days} दिनों`,

  'expense.category.food': 'खाना',
  'expense.category.travel': 'यात्रा',
  'expense.category.rent': 'किराया',
  'expense.category.utilities': 'बिजली-पानी',
  'expense.category.shopping': 'खरीदारी',
  'expense.category.other': 'अन्य',

  'expense.newEntry': 'नया खर्च',
  'expense.editEntry': 'खर्च बदलें',
  'expense.addNote': 'नोट जोड़ें (वैकल्पिक)',
  'expense.saveEntry': 'खर्च सेव करें',
  'expense.update': 'अपडेट करें',
  'expense.failedRecordEntry': 'खर्च दर्ज करने में विफल',
  'expense.failedUpdateEntry': 'खर्च अपडेट करने में विफल',
  'expense.failedDeleteEntry': 'खर्च हटाने में विफल',
  'expense.deleteEntryQ': 'यह खर्च हटाएं?',
  'expense.deleteEntryBody': 'यह पूर्ववत नहीं किया जा सकता।',

  'expenseDetail.title': 'खर्च का विवरण',
  'expenseDetail.note': 'नोट',

  'more.title': 'अधिक',
  'more.profileDesc': 'आपका नाम और फ़ोन',
  'more.settingsDesc': 'थीम और भाषा',
  'more.personalExpenses': 'व्यक्तिगत खर्च',
  'more.personalExpensesDesc': 'अपना रोज़ाना खर्च ट्रैक करें',
  'more.reports': 'रिपोर्ट',
  'more.reportsDesc': 'खर्च और व्यवसाय की जानकारी देखें',
  'more.notes': 'नोट्स',
  'more.notesDesc': 'त्वरित नोट्स लिखें',
  'more.reminders': 'रिमाइंडर',
  'more.remindersDesc': 'सही समय पर याद दिलाएं',

  'notes.title': 'नोट्स',
  'notes.searchNotes': 'नोट्स खोजें',
  'notes.noNotesYet': 'अभी कोई नोट नहीं',
  'notes.noNotesSub': 'अपना पहला नोट लिखने के लिए जोड़ें दबाएं।',

  'note.placeholder': 'लिखना शुरू करें...',
  'note.saving': 'सेव हो रहा है…',
  'note.saved': 'सेव हो गया',
  'note.deleteNoteQ': 'यह नोट हटाएं?',
  'note.deleteNoteBody': 'यह पूर्ववत नहीं किया जा सकता।',
  'note.failedSave': 'नोट सेव करने में विफल',
  'note.failedDelete': 'नोट हटाने में विफल',

  'reminders.title': 'रिमाइंडर',
  'reminders.new': 'नया रिमाइंडर',
  'reminders.reminder': 'रिमाइंडर',
  'reminders.upcoming': 'आने वाले',
  'reminders.past': 'पिछले',
  'reminders.today': 'आज',
  'reminders.tomorrow': 'कल',
  'reminders.yesterday': 'बीता कल',
  'reminders.later': 'बाद में',
  'reminders.emptyUpcoming': 'अभी कोई रिमाइंडर नहीं',
  'reminders.emptyUpcomingSub': 'Add दबाएं, हम सही समय पर याद दिलाएंगे।',
  'reminders.emptyPast': 'यहाँ अभी कुछ नहीं',
  'reminders.emptyPastSub': 'जो रिमाइंडर बज चुके हैं वे यहाँ दिखेंगे।',
  'reminders.titlePlaceholder': 'क्या याद दिलाना है?',
  'reminders.notesPlaceholder': 'विवरण जोड़ें (वैकल्पिक)',
  'reminders.when': 'कब',
  'reminders.repeat': 'दोहराएं',
  'reminders.repeat.none': 'एक बार',
  'reminders.repeat.daily': 'रोज़',
  'reminders.repeat.weekly': 'हर हफ्ते',
  'reminders.repeat.monthly': 'हर महीने',
  'reminders.early': 'पहले याद दिलाएं',
  'reminders.early.none': 'कोई नहीं',
  'reminders.early.5': '5 मिनट',
  'reminders.early.15': '15 मिनट',
  'reminders.early.30': '30 मिनट',
  'reminders.early.60': '1 घंटा',
  'reminders.early.1440': '1 दिन',
  'reminders.time': 'समय',
  'reminders.date': 'तारीख',
  'reminders.save': 'रिमाइंडर सेव करें',
  'reminders.remindedAt': (v) => `${v.when} याद दिलाया`,
  'reminders.snooze': 'स्नूज़',
  'reminders.snooze.10': '10 मिनट',
  'reminders.snooze.60': '1 घंटा',
  'reminders.snooze.1440': 'कल',
  'reminders.done': 'हो गया',
  'reminders.markDone': 'पूरा हुआ मार्क करें',
  'reminders.markDoneSub': 'इंतज़ार किए बिना पिछले में ले जाएं',
  'reminders.state.fired': 'याद दिलाया',
  'reminders.state.done': 'हो गया',
  'reminders.deleteQ': 'यह रिमाइंडर हटाना है?',
  'reminders.deleteBody': 'यह वापस नहीं हो सकता।',
  'reminders.failedSave': 'रिमाइंडर सेव नहीं हो सका',
  'reminders.failedUpdate': 'रिमाइंडर अपडेट नहीं हो सका',
  'reminders.failedDelete': 'रिमाइंडर हटाया नहीं जा सका',
  'reminders.notFound': 'यह रिमाइंडर अब मौजूद नहीं है।',
  'reminders.needTitle': 'क्या याद दिलाना है, वह लिखें।',
  'push.title': 'रिमाइंडर अलर्ट',
  'push.offBody': 'नोटिफिकेशन चालू करें ताकि Accountly बंद होने पर भी रिमाइंडर मिलें।',
  'push.onBody': 'इस डिवाइस पर रिमाइंडर अलर्ट मिलेंगे।',
  'push.installBody': 'iPhone पर रिमाइंडर अलर्ट के लिए Accountly को Home Screen पर जोड़ें।',
  'push.deniedBody': 'नोटिफिकेशन ब्लॉक हैं। ब्राउज़र सेटिंग्स में इस साइट के लिए इन्हें चालू करें।',
  'push.unsupportedBody': 'यह ब्राउज़र नोटिफिकेशन नहीं दिखा सकता।',
  'push.failed': 'नोटिफिकेशन चालू नहीं हो सके। फिर से कोशिश करें।',

  'profile.title': 'प्रोफ़ाइल',
  'profile.businessName': 'व्यवसाय का नाम',
  'profile.updated': 'प्रोफ़ाइल अपडेट हो गई',
  'profile.failedUpdate': 'प्रोफ़ाइल अपडेट करने में विफल',
  'profile.failedAvatarUpload': 'फ़ोटो अपलोड नहीं हो सकी',
  'profile.failedLogoUpload': 'लोगो अपलोड नहीं हो सका',
  'profile.businessLogo': 'व्यवसाय लोगो',
  'profile.businessLogoSub': 'बिल और साझा किए गए खाते में दिखेगा',

  'settings.title': 'सेटिंग्स',
  'settings.preferences': 'प्राथमिकताएं',
  'settings.darkMode': 'डार्क मोड',
  'settings.darkModeSub': 'गहरे रंग की थीम इस्तेमाल करें',
  'settings.language': 'भाषा',
  'settings.currency': 'मुद्रा',
  'settings.themeSaveFail': 'थीम सेव नहीं हो सकी',
  'settings.languageSaveFail': 'भाषा सेव नहीं हो सकी',
  'settings.currencySaveFail': 'मुद्रा सेव नहीं हो सकी। हो सकता है आपको इसे बदलने की अनुमति न हो।',

  'reports.title': 'रिपोर्ट',
  'reports.filters': 'फ़िल्टर',
  'reports.collected': 'प्राप्त',
  'reports.given': 'दिया',
  'reports.net': 'शुद्ध',
  'reports.trend': 'प्राप्त बनाम दिया',
  'reports.topContacts': 'शीर्ष संपर्क',
  'reports.noDataYet': 'इस अवधि में कोई लेन-देन नहीं',
  'reports.noDataSub': 'एक अलग तारीख सीमा आज़माएं।',

  'ledger.statementTitle': 'खाता विवरण',
  'ledger.generatedOn': 'बनाया गया',
  'ledger.currentBalance': 'वर्तमान शेष',
  'ledger.date': 'तारीख',
  'ledger.description': 'विवरण',
  'ledger.debit': 'डेबिट',
  'ledger.credit': 'क्रेडिट',
  'ledger.balance': 'शेष',
  'ledger.footer': 'Accountly के माध्यम से बनाया गया',
  'ledger.billTo': 'बिल प्राप्तकर्ता',
  'ledger.page': 'पृष्ठ',
  'ledger.of': 'में से',
  'ledger.amountDue': 'बकाया राशि',
  'ledger.creditBalance': 'जमा शेष',
  'ledger.givenTo': (v) => `${v.name} को दिया गया`,
  'ledger.receivedFrom': (v) => `${v.name} से प्राप्त हुआ`,

  'auth.welcomeBack': 'वापसी पर स्वागत है',
  'auth.signInToKhata': 'अपने खाते में साइन इन करें',
  'auth.enterPhoneNumber': 'फ़ोन नंबर डालें',
  'auth.password': 'पासवर्ड',
  'auth.signIn': 'साइन इन करें',
  'auth.newToAccountly': 'Accountly पर नए हैं?',
  'auth.createAccount': 'खाता बनाएं',
  'auth.loginFailed': 'लॉगिन विफल। फिर से कोशिश करें।',
  'auth.createYourAccount': 'अपना खाता बनाएं',
  'auth.startManaging': 'मिनटों में अपना खाता शुरू करें',
  'auth.businessName': 'व्यवसाय का नाम',
  'auth.yourName': 'आपका नाम',
  'auth.createAccountBtn': 'खाता बनाएं',
  'auth.creatingAccount': 'खाता बन रहा है…',
  'auth.signingIn': 'साइन इन हो रहा है…',
  'auth.alreadyHaveAccount': 'पहले से खाता है?',
  'auth.signInLink': 'साइन इन करें',
  'auth.registrationFailed': 'रजिस्ट्रेशन विफल। फिर से कोशिश करें।'
};

const gu: Dict = {
  'install.cardTitle': 'Accountly ઇન્સ્ટોલ કરો',
  'install.cardSub': 'ઝડપથી ખુલે છે અને ઑફલાઇન પણ ચાલે છે',
  'install.action': 'ઇન્સ્ટોલ',
  'install.samsungTitle': 'Chrome થી ઇન્સ્ટોલ કરો',
  'install.samsungStep1': 'આ લિંક કૉપિ કરીને Chrome માં ખોલો.',
  'install.samsungStep2': 'Chrome માં ઉપર જમણી બાજુ ⋮ મેનુ દબાવો.',
  'install.samsungStep3': '"Install app" દબાવો અને પુષ્ટિ કરો.',
  'install.copyLink': 'લિંક કૉપિ કરો',
  'install.copied': 'કૉપિ થઈ ગયું',
  'install.button': 'એપ ઇન્સ્ટોલ કરો',
  'install.desc': 'Accountly ને તમારી હોમ સ્ક્રીન પર ઉમેરો',
  'install.iosTitle': 'હોમ સ્ક્રીન પર ઉમેરો',
  'install.step1': 'તમારા બ્રાઉઝરની નીચે Share બટન દબાવો.',
  'install.step2': 'નીચે સ્ક્રોલ કરો અને "Add to Home Screen" દબાવો.',
  'install.step3': '"Add" દબાવો. હવે Accountly એપની જેમ ખુલશે.',
  'install.gotIt': 'સમજાઈ ગયું',
  'notifications.title': 'સૂચનાઓ',
  'notifications.emptyTitle': 'બધું અપડેટ છે',
  'notifications.emptySub': 'તમારી ચૂકવણી અને ખાતા સંબંધિત અપડેટ અહીં દેખાશે.',
  'due.add': 'તારીખ',
  'due.setDate': 'તારીખ નક્કી કરો',
  'due.title': 'બાકી',
  'due.today': 'આજે બાકી',
  'due.tomorrowLabel': 'કાલે બાકી',
  'due.inDays': (v) => `${v.days} દિવસમાં બાકી`,
  'due.overdueBy': (v) => `${v.days} દિવસ મોડું`,
  'due.failed': 'તારીખ અપડેટ થઈ શકી નથી',
  'common.save': 'સેવ કરો',
  'common.cancel': 'રદ કરો',
  'common.close': 'બંધ કરો',
  'common.delete': 'કાઢી નાખો',
  'common.edit': 'બદલો',
  'common.add': 'ઉમેરો',
  'common.search': 'શોધો',
  'common.seeAll': 'બધું જુઓ',
  'common.saving': 'સેવ થઈ રહ્યું છે…',
  'common.name': 'નામ',
  'common.phone': 'ફોન',
  'common.balance': 'બાકી રકમ',
  'common.details': 'વિગતો',
  'common.settings': 'સેટિંગ્સ',
  'common.apply': 'લાગુ કરો',
  'common.reset': 'રીસેટ કરો',

  'nav.home': 'હોમ',
  'nav.contacts': 'સંપર્કો',
  'nav.payments': 'વ્યવહારો',
  'nav.more': 'વધુ',

  'header.manageKhata': 'ખાતું મેનેજ કરો',
  'role.owner': 'માલિક',
  'role.admin': 'એડમિન',
  'role.staff': 'સ્ટાફ',
  'role.super_admin': 'સુપર એડમિન',

  'menu.profile': 'પ્રોફાઇલ',
  'menu.settings': 'સેટિંગ્સ',
  'menu.logout': 'લૉગઆઉટ',

  'home.youllGet': 'તમને મળશે',
  'home.youllGive': 'તમારે આપવાના',
  'home.viewFullReport': 'સંપૂર્ણ રિપોર્ટ જુઓ',
  'home.addContact': 'સંપર્ક ઉમેરો',
  'home.newContact': 'નવો સંપર્ક',
  'home.newEntry': 'નવો વ્યવહાર',
  'home.recordPayment': 'ચુકવણી નોંધો',
  'home.contacts': 'તાજેતરના સંપર્કો',
  'home.recentPayments': 'તાજેતરના વ્યવહારો',
  'home.noContactsYet': 'હજુ કોઈ સંપર્ક નથી',
  'home.noContactsSub': 'કોણ કેટલું આપે છે તે ટ્રૅક કરવા તમારો પહેલો સંપર્ક ઉમેરો.',
  'home.addContactCta': 'સંપર્ક ઉમેરો',
  'home.noPaymentsYet': 'હજુ કોઈ ચુકવણી નથી',
  'home.noPaymentsSub': 'કોઈ સંપર્ક ખોલો અને આપ્યા અથવા લીધા નોંધો, તે અહીં દેખાશે.',
  'home.paidTo': (v) => `${v.name} ને ઉધાર આપ્યા`,
  'home.receivedFrom': (v) => `${v.name} પાસેથી ઉધાર લીધા`,

  'contacts.title': 'સંપર્કો',
  'contactType.title': 'પ્રકાર',
  'contactType.customer': 'ગ્રાહક',
  'contactType.supplier': 'સપ્લાયર',
  'contactType.business': 'બિઝનેસ',
  'contactType.friend': 'મિત્ર',
  'contactType.family': 'પરિવાર',
  'contacts.searchPlaceholder': 'નામ અથવા ફોનથી શોધો',
  'contacts.noMatches': 'કંઈ મળ્યું નથી',
  'contacts.tryDifferent': 'બીજું નામ અથવા નંબર અજમાવો.',
  'contacts.tapAddToCreate': 'પહેલો સંપર્ક બનાવવા "ઉમેરો" દબાવો.',

  'detail.youWillGet': 'તમને મળશે',
  'detail.youWillGive': 'તમારે આપવાના',
  'detail.transactions': 'વ્યવહારો',
  'detail.recentTransactions': 'તાજેતરના વ્યવહારો',
  'detail.youGave': 'ઉધાર આપ્યા',
  'detail.youGot': 'ઉધાર લીધા',
  'detail.noTransactionsYet': 'હજુ કોઈ વ્યવહાર નથી',
  'detail.recordBelow': 'નીચે આપ્યા અથવા લીધા નોંધો.',
  'detail.shareLedger': 'ખાતું શેર કરો',
  'detail.failedGenerateLedger': 'ખાતું બનાવવામાં નિષ્ફળ',
  'detail.youGaveBtn': 'ઉધાર આપ્યા',
  'detail.youGotBtn': 'ઉધાર લીધા',
  'detail.sendReminderWhatsapp': 'વોટ્સએપ રિમાઇન્ડર',
  'detail.sendReminderSms': 'એસએમએસ રિમાઇન્ડર',
  'detail.reminderDue': (v) => `નમસ્તે ${v.name}, તમારે ${v.business} ને ${v.amount} ચૂકવવાના બાકી છે. કૃપા કરી જલ્દી ચૂકવો. આભાર!`,
  'detail.reminderOwed': (v) => `નમસ્તે ${v.name}, ${v.business} તરફથી તમને ${v.amount} આપવાના બાકી છે. અમે જલ્દી ચૂકવીશું. આભાર!`,

  'contactSettings.title': 'સંપર્ક સેટિંગ્સ',
  'contactSettings.notFound': 'સંપર્ક મળ્યો નથી.',
  'contactSettings.actions': 'ક્રિયાઓ',
  'contactSettings.editContact': 'સંપર્ક બદલો',
  'contactSettings.editContactSub': 'નામ, ફોન અથવા સરનામું બદલો',
  'contactSettings.deleteContact': 'સંપર્ક કાઢી નાખો',
  'contactSettings.deleteContactSub': 'આ સંપર્કને કાયમ માટે કાઢી નાખો',
  'contactSettings.deleteQ': 'સંપર્ક કાઢી નાખવો?',
  'contactSettings.deleteBody': (v) => `${v.name} અને તેમનો વ્યવહાર ઇતિહાસ કાઢી નખાશે. આ પાછું નહીં થાય.`,
  'contactSettings.failedDelete': 'સંપર્ક કાઢવામાં નિષ્ફળ',

  'contactForm.contactName': 'સંપર્કનું નામ',
  'contactForm.enterName': 'નામ લખો',
  'contactForm.nameRequired': 'નામ જરૂરી છે',
  'contactForm.phoneNumber': 'ફોન નંબર',
  'contactForm.enterPhoneNumber': 'ફોન નંબર દાખલ કરો',
  'contactForm.invalidPhone': 'યોગ્ય ફોન નંબર દાખલ કરો',
  'contactForm.address': 'સરનામું',
  'contactForm.optional': 'વૈકલ્પિક',
  'contactForm.addTitle': 'સંપર્ક ઉમેરો',
  'contactForm.editTitle': 'સંપર્ક બદલો',
  'contactForm.saveChanges': 'ફેરફારો સેવ કરો',
  'contactForm.businessInfoNotFound': 'બિઝનેસની માહિતી મળી નથી',
  'contactForm.failedAdd': 'સંપર્ક ઉમેરવામાં નિષ્ફળ',
  'contactForm.failedUpdate': 'સંપર્ક બદલવામાં નિષ્ફળ',
  'contactForm.failedImageUpload': 'ફોટો અપલોડ કરવામાં નિષ્ફળ',
  'contactForm.alreadyExists': 'આ ફોન નંબરથી સંપર્ક પહેલેથી છે',
  'contactForm.selectCountry': 'દેશ પસંદ કરો',
  'contactForm.searchCountries': 'દેશ શોધો',

  'payment.editEntry': 'વ્યવહાર બદલો',
  'payment.youGaveTitle': 'ઉધાર આપ્યા',
  'payment.youGotTitle': 'ઉધાર લીધા',
  'payment.to': 'ને',
  'payment.from': 'પાસેથી',
  'payment.addNote': 'નોંધ ઉમેરો (વૈકલ્પિક)',
  'payment.update': 'અપડેટ કરો',
  'payment.saveEntry': 'વ્યવહાર સેવ કરો',
  'payment.entrySaved': 'વ્યવહાર સેવ થયો',
  'payment.contactNotFound': 'સંપર્ક મળ્યો નથી',
  'payment.failedUpdateEntry': 'વ્યવહાર અપડેટ કરવામાં નિષ્ફળ',
  'payment.failedRecordEntry': 'વ્યવહાર નોંધવામાં નિષ્ફળ',
  'payment.failedDeleteEntry': 'વ્યવહાર કાઢવામાં નિષ્ફળ',
  'payment.deleteEntryQ': 'આ વ્યવહાર કાઢી નાખવો?',
  'payment.deleteEntryBody': 'આનાથી સંપર્કની બાકી રકમ બદલાશે. આ પાછું નહીં થાય.',
  'payment.date': 'તારીખ',
  'payment.attachBills': 'ફોટો જોડો',
  'payment.imageAttached': 'ફોટો જોડાયો',
  'payment.failedAttachImage': 'ફોટો જોડવામાં નિષ્ફળ',
  'payment.comingSoon': 'બિલ જોડવાની સુવિધા ટૂંક સમયમાં આવશે',
  'payment.tapToUse': 'ઉપયોગ કરવા ટેપ કરો',
  'transactionDetail.title': 'વ્યવહારની વિગતો',
  'transactionDetail.note': 'નોંધ',
  'transactionDetail.attachment': 'એટેચમેન્ટ',
  'transactionDetail.viewAttachment': 'એટેચમેન્ટ જુઓ',

  'transactions.title': 'વ્યવહારો',
  'transactions.searchPayments': 'ચુકવણીઓ શોધો',
  'transactions.openContactRecord': 'કોઈ સંપર્ક ખોલો અને આપ્યા અથવા લીધા નોંધો.',
  'transactions.filters': 'ફિલ્ટર',
  'transactions.dateRangeLabel': 'તારીખ શ્રેણી',
  'transactions.typeLabel': 'પ્રકાર',
  'transactions.filterToday': 'આજે',
  'transactions.filterYesterday': 'ગઈકાલે',
  'transactions.filterThisWeek': 'આ અઠવાડિયે',
  'transactions.filterThisMonth': 'આ મહિને',
  'transactions.filterLastMonth': 'ગયો મહિનો',
  'transactions.filterAllTime': 'બધો સમય',
  'transactions.filterCustomRange': 'કસ્ટમ રેન્જ',
  'transactions.filterAllTypes': 'બધા',
  'transactions.startDate': 'શરૂઆતની તારીખ',
  'transactions.endDate': 'અંતિમ તારીખ',

  'expenses.title': 'અંગત ખર્ચ',
  'expenses.searchExpenses': 'ખર્ચ શોધો',
  'expenses.noExpensesYet': 'હજુ કોઈ ખર્ચ નથી',
  'expenses.noExpensesSub': 'તમારો પહેલો અંગત ખર્ચ ઉમેરવા માટે ઉમેરો દબાવો.',
  'expenses.filters': 'ફિલ્ટર',
  'expenses.categoryLabel': 'શ્રેણી',
  'expenses.totalSpent': 'કુલ ખર્ચ',
  'expenses.moreThan': (v) => `${v.period} કરતાં ${v.amount} વધુ`,
  'expenses.lessThan': (v) => `${v.period} કરતાં ${v.amount} ઓછું`,
  'expenses.sameAs': (v) => `${v.period} જેટલું જ`,
  'expenses.cmpYesterday': 'ગઈકાલ',
  'expenses.cmpDayBefore': 'પરમ દિવસ',
  'expenses.cmpLastWeek': 'ગયા અઠવાડિયે આ સમય',
  'expenses.cmpLastMonth': 'ગયા મહિને આ સમય',
  'expenses.cmpMonthBefore': 'તેની પહેલાંનો મહિનો',
  'expenses.cmpPreviousDays': (v) => `પાછલા ${v.days} દિવસ`,

  'expense.category.food': 'ખોરાક',
  'expense.category.travel': 'મુસાફરી',
  'expense.category.rent': 'ભાડું',
  'expense.category.utilities': 'વીજળી-પાણી',
  'expense.category.shopping': 'ખરીદી',
  'expense.category.other': 'અન્ય',

  'expense.newEntry': 'નવો ખર્ચ',
  'expense.editEntry': 'ખર્ચ બદલો',
  'expense.addNote': 'નોંધ ઉમેરો (વૈકલ્પિક)',
  'expense.saveEntry': 'ખર્ચ સેવ કરો',
  'expense.update': 'અપડેટ કરો',
  'expense.failedRecordEntry': 'ખર્ચ નોંધવામાં નિષ્ફળ',
  'expense.failedUpdateEntry': 'ખર્ચ અપડેટ કરવામાં નિષ્ફળ',
  'expense.failedDeleteEntry': 'ખર્ચ કાઢવામાં નિષ્ફળ',
  'expense.deleteEntryQ': 'આ ખર્ચ કાઢી નાખવો છે?',
  'expense.deleteEntryBody': 'આ પાછું લઈ શકાશે નહીં.',

  'expenseDetail.title': 'ખર્ચની વિગતો',
  'expenseDetail.note': 'નોંધ',

  'more.title': 'વધુ',
  'more.profileDesc': 'તમારું નામ અને ફોન',
  'more.settingsDesc': 'થીમ અને ભાષા',
  'more.personalExpenses': 'અંગત ખર્ચ',
  'more.personalExpensesDesc': 'તમારો રોજિંદો ખર્ચ ટ્રેક કરો',
  'more.reports': 'રિપોર્ટ',
  'more.reportsDesc': 'ખર્ચ અને વ્યવસાયની માહિતી જુઓ',
  'more.notes': 'નોંધ',
  'more.notesDesc': 'ઝડપી નોંધ લખો',
  'more.reminders': 'રિમાઇન્ડર',
  'more.remindersDesc': 'યોગ્ય સમયે યાદ અપાવો',

  'notes.title': 'નોંધ',
  'notes.searchNotes': 'નોંધ શોધો',
  'notes.noNotesYet': 'હજુ કોઈ નોંધ નથી',
  'notes.noNotesSub': 'તમારી પહેલી નોંધ લખવા માટે ઉમેરો દબાવો.',

  'note.placeholder': 'લખવાનું શરૂ કરો...',
  'note.saving': 'સેવ થઈ રહ્યું છે…',
  'note.saved': 'સેવ થયું',
  'note.deleteNoteQ': 'આ નોંધ કાઢી નાખવી છે?',
  'note.deleteNoteBody': 'આ પૂર્વવત્ કરી શકાશે નહીં.',
  'note.failedSave': 'નોંધ સેવ કરવામાં નિષ્ફળ',
  'note.failedDelete': 'નોંધ કાઢી નાખવામાં નિષ્ફળ',

  'reminders.title': 'રિમાઇન્ડર',
  'reminders.new': 'નવું રિમાઇન્ડર',
  'reminders.reminder': 'રિમાઇન્ડર',
  'reminders.upcoming': 'આવનારા',
  'reminders.past': 'પાછલા',
  'reminders.today': 'આજે',
  'reminders.tomorrow': 'આવતીકાલે',
  'reminders.yesterday': 'ગઈકાલે',
  'reminders.later': 'પછી',
  'reminders.emptyUpcoming': 'હજી કોઈ રિમાઇન્ડર નથી',
  'reminders.emptyUpcomingSub': 'Add દબાવો, અમે યોગ્ય સમયે યાદ અપાવીશું.',
  'reminders.emptyPast': 'અહીં હજી કંઈ નથી',
  'reminders.emptyPastSub': 'જે રિમાઇન્ડર વાગી ગયા છે તે અહીં દેખાશે.',
  'reminders.titlePlaceholder': 'શું યાદ અપાવવું છે?',
  'reminders.notesPlaceholder': 'વિગતો ઉમેરો (વૈકલ્પિક)',
  'reminders.when': 'ક્યારે',
  'reminders.repeat': 'પુનરાવર્તન',
  'reminders.repeat.none': 'એક વાર',
  'reminders.repeat.daily': 'દરરોજ',
  'reminders.repeat.weekly': 'દર અઠવાડિયે',
  'reminders.repeat.monthly': 'દર મહિને',
  'reminders.early': 'વહેલું રિમાઇન્ડર',
  'reminders.early.none': 'કોઈ નહીં',
  'reminders.early.5': '5 મિનિટ',
  'reminders.early.15': '15 મિનિટ',
  'reminders.early.30': '30 મિનિટ',
  'reminders.early.60': '1 કલાક',
  'reminders.early.1440': '1 દિવસ',
  'reminders.time': 'સમય',
  'reminders.date': 'તારીખ',
  'reminders.save': 'રિમાઇન્ડર સેવ કરો',
  'reminders.remindedAt': (v) => `${v.when} યાદ અપાવ્યું`,
  'reminders.snooze': 'સ્નૂઝ',
  'reminders.snooze.10': '10 મિનિટ',
  'reminders.snooze.60': '1 કલાક',
  'reminders.snooze.1440': 'કાલે',
  'reminders.done': 'થઈ ગયું',
  'reminders.markDone': 'પૂર્ણ તરીકે માર્ક કરો',
  'reminders.markDoneSub': 'રાહ જોયા વિના પાછલામાં ખસેડો',
  'reminders.state.fired': 'યાદ અપાવ્યું',
  'reminders.state.done': 'થઈ ગયું',
  'reminders.deleteQ': 'આ રિમાઇન્ડર કાઢી નાખવું છે?',
  'reminders.deleteBody': 'આ પાછું લાવી શકાશે નહીં.',
  'reminders.failedSave': 'રિમાઇન્ડર સેવ ન થઈ શક્યું',
  'reminders.failedUpdate': 'રિમાઇન્ડર અપડેટ ન થઈ શક્યું',
  'reminders.failedDelete': 'રિમાઇન્ડર કાઢી ન શકાયું',
  'reminders.notFound': 'આ રિમાઇન્ડર હવે અસ્તિત્વમાં નથી.',
  'reminders.needTitle': 'શું યાદ અપાવવું છે તે લખો.',
  'push.title': 'રિમાઇન્ડર એલર્ટ',
  'push.offBody': 'નોટિફિકેશન ચાલુ કરો જેથી Accountly બંધ હોય ત્યારે પણ રિમાઇન્ડર મળે.',
  'push.onBody': 'આ ડિવાઇસ પર રિમાઇન્ડર એલર્ટ મળશે.',
  'push.installBody': 'iPhone પર રિમાઇન્ડર એલર્ટ માટે Accountly ને Home Screen પર ઉમેરો.',
  'push.deniedBody': 'નોટિફિકેશન બ્લોક છે. બ્રાઉઝર સેટિંગ્સમાં આ સાઇટ માટે ચાલુ કરો.',
  'push.unsupportedBody': 'આ બ્રાઉઝર નોટિફિકેશન બતાવી શકતું નથી.',
  'push.failed': 'નોટિફિકેશન ચાલુ ન થઈ શક્યા. ફરી પ્રયાસ કરો.',

  'profile.title': 'પ્રોફાઇલ',
  'profile.businessName': 'બિઝનેસનું નામ',
  'profile.updated': 'પ્રોફાઇલ અપડેટ થઈ',
  'profile.failedUpdate': 'પ્રોફાઇલ અપડેટ કરવામાં નિષ્ફળ',
  'profile.failedAvatarUpload': 'ફોટો અપલોડ કરવામાં નિષ્ફળ',
  'profile.failedLogoUpload': 'લોગો અપલોડ કરવામાં નિષ્ફળ',
  'profile.businessLogo': 'બિઝનેસ લોગો',
  'profile.businessLogoSub': 'બિલ અને શેર કરેલા ખાતામાં દેખાશે',

  'settings.title': 'સેટિંગ્સ',
  'settings.preferences': 'પસંદગીઓ',
  'settings.darkMode': 'ડાર્ક મોડ',
  'settings.darkModeSub': 'ઘાટા રંગની થીમ વાપરો',
  'settings.language': 'ભાષા',
  'settings.currency': 'ચલણ',
  'settings.themeSaveFail': 'થીમ સેવ ન થઈ શકી',
  'settings.languageSaveFail': 'ભાષા સેવ ન થઈ શકી',
  'settings.currencySaveFail': 'ચલણ સેવ ન થઈ શક્યું. કદાચ તમને બદલવાની પરવાનગી નથી.',

  'reports.title': 'રિપોર્ટ',
  'reports.filters': 'ફિલ્ટર',
  'reports.collected': 'મળ્યું',
  'reports.given': 'આપ્યું',
  'reports.net': 'ચોખ્ખું',
  'reports.trend': 'મળ્યું વિ. આપ્યું',
  'reports.topContacts': 'ટોચના સંપર્કો',
  'reports.noDataYet': 'આ સમયગાળામાં કોઈ વ્યવહાર નથી',
  'reports.noDataSub': 'અલગ તારીખ શ્રેણી અજમાવો.',

  'ledger.statementTitle': 'ખાતાનું સ્ટેટમેન્ટ',
  'ledger.generatedOn': 'બનાવ્યા તારીખ',
  'ledger.currentBalance': 'હાલની બાકી',
  'ledger.date': 'તારીખ',
  'ledger.description': 'વિગત',
  'ledger.debit': 'ડેબિટ',
  'ledger.credit': 'ક્રેડિટ',
  'ledger.balance': 'બાકી',
  'ledger.footer': 'Accountly દ્વારા બનાવેલ',
  'ledger.billTo': 'બિલ પ્રાપ્તકર્તા',
  'ledger.page': 'પાનું',
  'ledger.of': 'માંથી',
  'ledger.amountDue': 'બાકી રકમ',
  'ledger.creditBalance': 'જમા બાકી',
  'ledger.givenTo': (v) => `${v.name} ને આપ્યું`,
  'ledger.receivedFrom': (v) => `${v.name} પાસેથી મળ્યું`,

  'auth.welcomeBack': 'પાછા સ્વાગત છે',
  'auth.signInToKhata': 'તમારા ખાતામાં સાઇન ઇન કરો',
  'auth.enterPhoneNumber': 'ફોન નંબર દાખલ કરો',
  'auth.password': 'પાસવર્ડ',
  'auth.signIn': 'સાઇન ઇન કરો',
  'auth.newToAccountly': 'Accountly પર નવા છો?',
  'auth.createAccount': 'ખાતું બનાવો',
  'auth.loginFailed': 'લૉગિન નિષ્ફળ. ફરી પ્રયાસ કરો.',
  'auth.createYourAccount': 'તમારું ખાતું બનાવો',
  'auth.startManaging': 'મિનિટોમાં તમારું ખાતું મેનેજ કરવાનું શરૂ કરો',
  'auth.businessName': 'બિઝનેસનું નામ',
  'auth.yourName': 'તમારું નામ',
  'auth.createAccountBtn': 'ખાતું બનાવો',
  'auth.creatingAccount': 'ખાતું બની રહ્યું છે…',
  'auth.signingIn': 'સાઇન ઇન થઈ રહ્યું છે…',
  'auth.alreadyHaveAccount': 'પહેલેથી ખાતું છે?',
  'auth.signInLink': 'સાઇન ઇન કરો',
  'auth.registrationFailed': 'રજિસ્ટ્રેશન નિષ્ફળ. ફરી પ્રયાસ કરો.'
};

const hiLatn: Dict = {
  'install.cardTitle': 'Accountly install karo',
  'install.cardSub': 'Tez khulta hai aur offline bhi chalta hai',
  'install.action': 'Install',
  'install.samsungTitle': 'Chrome se install karo',
  'install.samsungStep1': 'Is link ko copy karke Chrome mein kholo.',
  'install.samsungStep2': 'Chrome mein upar daayin taraf ⋮ menu dabao.',
  'install.samsungStep3': '"Install app" dabao aur confirm karo.',
  'install.copyLink': 'Link copy karo',
  'install.copied': 'Copy ho gaya',
  'install.button': 'App install karo',
  'install.desc': 'Accountly ko apni home screen par jodo',
  'install.iosTitle': 'Home Screen par jodo',
  'install.step1': 'Apne browser ke neeche Share button dabao.',
  'install.step2': 'Neeche scroll karo aur "Add to Home Screen" dabao.',
  'install.step3': '"Add" dabao. Ab Accountly app ki tarah khulega.',
  'install.gotIt': 'Samajh gaya',
  'notifications.title': 'Suchnayein',
  'notifications.emptyTitle': 'Sab kuch update hai',
  'notifications.emptySub': 'Aapke payment aur account se jude update yahan dikhenge.',
  'due.add': 'Tareekh',
  'due.setDate': 'Tareekh tay karo',
  'due.title': 'Baaki',
  'due.today': 'Aaj dena hai',
  'due.tomorrowLabel': 'Kal dena hai',
  'due.inDays': (v) => `${v.days} din mein dena hai`,
  'due.overdueBy': (v) => `${v.days} din ki deri`,
  'due.failed': 'Tareekh update nahi ho saki',
  'common.save': 'Save karo',
  'common.cancel': 'Cancel karo',
  'common.close': 'Band karo',
  'common.delete': 'Delete karo',
  'common.edit': 'Edit karo',
  'common.add': 'Add karo',
  'common.search': 'Dhundo',
  'common.seeAll': 'Sab dekho',
  'common.saving': 'Save ho raha hai…',
  'common.name': 'Naam',
  'common.phone': 'Phone',
  'common.balance': 'Balance',
  'common.details': 'Details',
  'common.settings': 'Settings',
  'common.apply': 'Apply Karo',
  'common.reset': 'Reset Karo',

  'nav.home': 'Home',
  'nav.contacts': 'Contacts',
  'nav.payments': 'Transactions',
  'nav.more': 'More',

  'header.manageKhata': 'Khata manage karo',
  'role.owner': 'Malik',
  'role.admin': 'Admin',
  'role.staff': 'Staff',
  'role.super_admin': 'Super Admin',

  'menu.profile': 'Profile',
  'menu.settings': 'Settings',
  'menu.logout': 'Logout',

  'home.youllGet': 'Tumhe Milega',
  'home.youllGive': 'Tumhe Dena Hai',
  'home.viewFullReport': 'Pura report dekho',
  'home.addContact': 'Contact Jodo',
  'home.newContact': 'Naya contact',
  'home.newEntry': 'Naya Transaction',
  'home.recordPayment': 'Payment record karo',
  'home.contacts': 'Recent Contacts',
  'home.recentPayments': 'Recent Transactions',
  'home.noContactsYet': 'Abhi koi contact nahi hai',
  'home.noContactsSub': 'Kaun kitna deta hai jaanne ke liye apna pehla contact jodo.',
  'home.addContactCta': 'Contact jodo',
  'home.noPaymentsYet': 'Abhi koi payment nahi hai',
  'home.noPaymentsSub': 'Kisi contact ko kholo aur Send ya Receive record karo, yahan dikhega.',
  'home.paidTo': (v) => `${v.name} ko udhar diye`,
  'home.receivedFrom': (v) => `${v.name} se udhar liye`,

  'contacts.title': 'Contacts',
  'contactType.title': 'Type',
  'contactType.customer': 'Customer',
  'contactType.supplier': 'Supplier',
  'contactType.business': 'Business',
  'contactType.friend': 'Dost',
  'contactType.family': 'Parivar',
  'contacts.searchPlaceholder': 'Naam ya phone se dhundo',
  'contacts.noMatches': 'Kuch nahi mila',
  'contacts.tryDifferent': 'Doosra naam ya number try karo.',
  'contacts.tapAddToCreate': 'Pehla contact banane ke liye Add dabao.',

  'detail.youWillGet': 'Tumhe Milega',
  'detail.youWillGive': 'Tumhe Dena Hai',
  'detail.transactions': 'Transactions',
  'detail.recentTransactions': 'Recent Transactions',
  'detail.youGave': 'Udhar Diye',
  'detail.youGot': 'Udhar Liye',
  'detail.noTransactionsYet': 'Abhi koi transaction nahi hai',
  'detail.recordBelow': 'Niche Send ya Receive record karo.',
  'detail.shareLedger': 'Khata Share Karo',
  'detail.failedGenerateLedger': 'Khata banane me fail',
  'detail.youGaveBtn': 'Udhar Diye',
  'detail.youGotBtn': 'Udhar Liye',
  'detail.sendReminderWhatsapp': 'WhatsApp Reminder',
  'detail.sendReminderSms': 'SMS Reminder',
  'detail.reminderDue': (v) => `Hi ${v.name}, ${v.business} ko ${v.amount} ka udhar dena baaki hai. Please jald pay karein. Dhanyawad!`,
  'detail.reminderOwed': (v) => `Hi ${v.name}, ${v.business} ki taraf se aapko ${v.amount} dene hain. Hum jald settle kar denge. Dhanyawad!`,

  'contactSettings.title': 'Contact Settings',
  'contactSettings.notFound': 'Contact nahi mila.',
  'contactSettings.actions': 'Actions',
  'contactSettings.editContact': 'Contact Edit Karo',
  'contactSettings.editContactSub': 'Naam, phone ya address badlo',
  'contactSettings.deleteContact': 'Contact Delete Karo',
  'contactSettings.deleteContactSub': 'Is contact ko hamesha ke liye hatao',
  'contactSettings.deleteQ': 'Contact delete karna hai?',
  'contactSettings.deleteBody': (v) => `${v.name} aur unki transaction history hat jayegi. Ye wapas nahi hoga.`,
  'contactSettings.failedDelete': 'Contact delete nahi ho paya',

  'contactForm.contactName': 'Contact Ka Naam',
  'contactForm.enterName': 'Naam likho',
  'contactForm.nameRequired': 'Naam zaroori hai',
  'contactForm.phoneNumber': 'Phone Number',
  'contactForm.enterPhoneNumber': 'Phone number daalo',
  'contactForm.invalidPhone': 'Sahi phone number daalo',
  'contactForm.address': 'Address',
  'contactForm.optional': 'Optional',
  'contactForm.addTitle': 'Contact Jodo',
  'contactForm.editTitle': 'Contact Edit Karo',
  'contactForm.saveChanges': 'Changes Save Karo',
  'contactForm.businessInfoNotFound': 'Business ki jaankari nahi mili',
  'contactForm.failedAdd': 'Contact add nahi ho paya',
  'contactForm.failedUpdate': 'Contact update nahi ho paya',
  'contactForm.failedImageUpload': 'Photo upload nahi ho paya',
  'contactForm.alreadyExists': 'Is phone number se contact pehle se hai',
  'contactForm.selectCountry': 'Desh Chuno',
  'contactForm.searchCountries': 'Desh dhundo',

  'payment.editEntry': 'Transaction Edit Karo',
  'payment.youGaveTitle': 'Udhar Diye',
  'payment.youGotTitle': 'Udhar Liye',
  'payment.to': 'ko',
  'payment.from': 'se',
  'payment.addNote': 'Note jodo (optional)',
  'payment.update': 'Update Karo',
  'payment.saveEntry': 'Transaction Save Karo',
  'payment.entrySaved': 'Transaction Save Ho Gaya',
  'payment.contactNotFound': 'Contact nahi mila',
  'payment.failedUpdateEntry': 'Transaction update nahi ho paya',
  'payment.failedRecordEntry': 'Transaction record nahi ho paya',
  'payment.failedDeleteEntry': 'Transaction delete nahi ho paya',
  'payment.deleteEntryQ': 'Ye transaction delete karna hai?',
  'payment.deleteEntryBody': 'Isse contact ka balance badal jayega. Ye wapas nahi hoga.',
  'payment.date': 'Date',
  'payment.attachBills': 'Photo Jodo',
  'payment.imageAttached': 'Photo jud gaya',
  'payment.failedAttachImage': 'Photo jodne mein fail hua',
  'payment.comingSoon': 'Bill jodne ki suvidha jald aa rahi hai',
  'payment.tapToUse': 'Use karne ke liye tap karo',
  'transactionDetail.title': 'Transaction Details',
  'transactionDetail.note': 'Note',
  'transactionDetail.attachment': 'Attachment',
  'transactionDetail.viewAttachment': 'Attachment Dekho',

  'transactions.title': 'Transactions',
  'transactions.searchPayments': 'Payments dhundo',
  'transactions.openContactRecord': 'Kisi contact ko kholo aur Send ya Receive record karo.',
  'transactions.filters': 'Filters',
  'transactions.dateRangeLabel': 'Date Range',
  'transactions.typeLabel': 'Type',
  'transactions.filterToday': 'Aaj',
  'transactions.filterYesterday': 'Kal',
  'transactions.filterThisWeek': 'Is Hafte',
  'transactions.filterThisMonth': 'Is Mahine',
  'transactions.filterLastMonth': 'Pichhla Mahina',
  'transactions.filterAllTime': 'Sab Time',
  'transactions.filterCustomRange': 'Custom Range',
  'transactions.filterAllTypes': 'Sab',
  'transactions.startDate': 'Shuru Ki Tarikh',
  'transactions.endDate': 'Aakhri Tarikh',

  'expenses.title': 'Personal Kharch',
  'expenses.searchExpenses': 'Kharch dhundo',
  'expenses.noExpensesYet': 'Abhi koi kharch nahi hai',
  'expenses.noExpensesSub': 'Apna pehla personal kharch jodne ke liye Add dabao.',
  'expenses.filters': 'Filters',
  'expenses.categoryLabel': 'Category',
  'expenses.totalSpent': 'Kul kharch',
  'expenses.moreThan': (v) => `${v.period} se ${v.amount} zyada`,
  'expenses.lessThan': (v) => `${v.period} se ${v.amount} kam`,
  'expenses.sameAs': (v) => `${v.period} jitna hi`,
  'expenses.cmpYesterday': 'kal',
  'expenses.cmpDayBefore': 'parson',
  'expenses.cmpLastWeek': 'pichhle hafte isi samay',
  'expenses.cmpLastMonth': 'pichhle mahine isi samay',
  'expenses.cmpMonthBefore': 'usse pichhla mahina',
  'expenses.cmpPreviousDays': (v) => `pichhle ${v.days} din`,

  'expense.category.food': 'Khana',
  'expense.category.travel': 'Travel',
  'expense.category.rent': 'Kiraya',
  'expense.category.utilities': 'Bijli-Pani',
  'expense.category.shopping': 'Shopping',
  'expense.category.other': 'Other',

  'expense.newEntry': 'Naya Kharch',
  'expense.editEntry': 'Kharch Badlo',
  'expense.addNote': 'Note jodo (optional)',
  'expense.saveEntry': 'Kharch Save Karo',
  'expense.update': 'Update Karo',
  'expense.failedRecordEntry': 'Kharch record karne me fail',
  'expense.failedUpdateEntry': 'Kharch update karne me fail',
  'expense.failedDeleteEntry': 'Kharch delete karne me fail',
  'expense.deleteEntryQ': 'Ye kharch delete karna hai?',
  'expense.deleteEntryBody': 'Ye wapas nahi ho sakta.',

  'expenseDetail.title': 'Kharch Details',
  'expenseDetail.note': 'Note',

  'more.title': 'More',
  'more.profileDesc': 'Tumhara naam aur phone',
  'more.settingsDesc': 'Theme aur language',
  'more.personalExpenses': 'Personal Kharch',
  'more.personalExpensesDesc': 'Apna daily kharch track karo',
  'more.reports': 'Reports',
  'more.reportsDesc': 'Kharch aur business insights dekho',
  'more.notes': 'Notes',
  'more.notesDesc': 'Jaldi notes likho',
  'more.reminders': 'Reminders',
  'more.remindersDesc': 'Sahi time pe yaad dilao',

  'notes.title': 'Notes',
  'notes.searchNotes': 'Notes dhundo',
  'notes.noNotesYet': 'Abhi koi note nahi hai',
  'notes.noNotesSub': 'Apna pehla note likhne ke liye Add dabao.',

  'note.placeholder': 'Likhna shuru karo...',
  'note.saving': 'Save ho raha hai…',
  'note.saved': 'Save ho gaya',
  'note.deleteNoteQ': 'Ye note delete karna hai?',
  'note.deleteNoteBody': 'Ye wapas nahi ho sakta.',
  'note.failedSave': 'Note save karne me fail',
  'note.failedDelete': 'Note delete karne me fail',

  'reminders.title': 'Reminders',
  'reminders.new': 'Naya reminder',
  'reminders.reminder': 'Reminder',
  'reminders.upcoming': 'Aane wale',
  'reminders.past': 'Pichhle',
  'reminders.today': 'Aaj',
  'reminders.tomorrow': 'Kal',
  'reminders.yesterday': 'Beeta kal',
  'reminders.later': 'Baad me',
  'reminders.emptyUpcoming': 'Abhi koi reminder nahi',
  'reminders.emptyUpcomingSub': 'Add dabao, hum sahi time pe yaad dilayenge.',
  'reminders.emptyPast': 'Yahan abhi kuch nahi',
  'reminders.emptyPastSub': 'Jo reminders baj chuke hain wo yahan dikhenge.',
  'reminders.titlePlaceholder': 'Kya yaad dilana hai?',
  'reminders.notesPlaceholder': 'Details likho (optional)',
  'reminders.when': 'Kab',
  'reminders.repeat': 'Repeat',
  'reminders.repeat.none': 'Ek baar',
  'reminders.repeat.daily': 'Roz',
  'reminders.repeat.weekly': 'Har hafte',
  'reminders.repeat.monthly': 'Har mahine',
  'reminders.early': 'Pehle yaad dilao',
  'reminders.early.none': 'Koi nahi',
  'reminders.early.5': '5 min',
  'reminders.early.15': '15 min',
  'reminders.early.30': '30 min',
  'reminders.early.60': '1 ghanta',
  'reminders.early.1440': '1 din',
  'reminders.time': 'Time',
  'reminders.date': 'Date',
  'reminders.save': 'Reminder save karo',
  'reminders.remindedAt': (v) => `${v.when} yaad dilaya`,
  'reminders.snooze': 'Snooze',
  'reminders.snooze.10': '10 min',
  'reminders.snooze.60': '1 ghanta',
  'reminders.snooze.1440': 'Kal',
  'reminders.done': 'Ho gaya',
  'reminders.markDone': 'Done mark karo',
  'reminders.markDoneSub': 'Bina ruke Pichhle me le jao',
  'reminders.state.fired': 'Yaad dilaya',
  'reminders.state.done': 'Ho gaya',
  'reminders.deleteQ': 'Ye reminder delete karna hai?',
  'reminders.deleteBody': 'Ye wapas nahi ho sakta.',
  'reminders.failedSave': 'Reminder save nahi ho paya',
  'reminders.failedUpdate': 'Reminder update nahi ho paya',
  'reminders.failedDelete': 'Reminder delete nahi ho paya',
  'reminders.notFound': 'Ye reminder ab nahi hai.',
  'reminders.needTitle': 'Kya yaad dilana hai wo likho.',
  'push.title': 'Reminder alerts',
  'push.offBody': 'Notifications on karo taaki Accountly band ho tab bhi reminder mile.',
  'push.onBody': 'Is device pe reminder alerts milenge.',
  'push.installBody': 'iPhone pe reminder alerts ke liye Accountly ko Home Screen pe add karo.',
  'push.deniedBody': 'Notifications block hain. Browser settings me is site ke liye allow karo.',
  'push.unsupportedBody': 'Ye browser notifications nahi dikha sakta.',
  'push.failed': 'Notifications on nahi ho paye. Fir se try karo.',

  'profile.title': 'Profile',
  'profile.businessName': 'Business Ka Naam',
  'profile.updated': 'Profile update ho gaya',
  'profile.failedUpdate': 'Profile update nahi ho paya',
  'profile.failedAvatarUpload': 'Photo upload nahi ho paya',
  'profile.failedLogoUpload': 'Logo upload nahi ho paya',
  'profile.businessLogo': 'Business Logo',
  'profile.businessLogoSub': 'Bills aur shared ledger me dikhega',

  'settings.title': 'Settings',
  'settings.preferences': 'Preferences',
  'settings.darkMode': 'Dark Mode',
  'settings.darkModeSub': 'Dark color theme use karo',
  'settings.language': 'Language',
  'settings.currency': 'Currency',
  'settings.themeSaveFail': 'Theme save nahi ho payi',
  'settings.languageSaveFail': 'Language save nahi ho payi',
  'settings.currencySaveFail': 'Currency save nahi ho payi. Shayad tumhe badalne ki permission nahi hai.',

  'reports.title': 'Reports',
  'reports.filters': 'Filters',
  'reports.collected': 'Mila',
  'reports.given': 'Diya',
  'reports.net': 'Net',
  'reports.trend': 'Mila vs Diya',
  'reports.topContacts': 'Top Contacts',
  'reports.noDataYet': 'Is period me koi transaction nahi',
  'reports.noDataSub': 'Alag date range try karo.',

  'ledger.statementTitle': 'Khata Statement',
  'ledger.generatedOn': 'Banaya gaya',
  'ledger.currentBalance': 'Current Balance',
  'ledger.date': 'Date',
  'ledger.description': 'Description',
  'ledger.debit': 'Debit',
  'ledger.credit': 'Credit',
  'ledger.balance': 'Balance',
  'ledger.footer': 'Accountly se generate kiya gaya',
  'ledger.billTo': 'Bill To',
  'ledger.page': 'Page',
  'ledger.of': 'of',
  'ledger.amountDue': 'Amount Due',
  'ledger.creditBalance': 'Credit Balance',
  'ledger.givenTo': (v) => `${v.name} ko diya gaya`,
  'ledger.receivedFrom': (v) => `${v.name} se mila`,

  'auth.welcomeBack': 'Wapas aane ka swagat hai',
  'auth.signInToKhata': 'Apne khata mein sign in karo',
  'auth.enterPhoneNumber': 'Phone number daalo',
  'auth.password': 'Password',
  'auth.signIn': 'Sign In',
  'auth.newToAccountly': 'Accountly pe naye ho?',
  'auth.createAccount': 'Account banao',
  'auth.loginFailed': 'Login fail ho gaya. Phir try karo.',
  'auth.createYourAccount': 'Apna account banao',
  'auth.startManaging': 'Minuton mein apna khata manage karna shuru karo',
  'auth.businessName': 'Business ka naam',
  'auth.yourName': 'Tumhara naam',
  'auth.createAccountBtn': 'Account Banao',
  'auth.creatingAccount': 'Account ban raha hai…',
  'auth.signingIn': 'Sign in ho raha hai…',
  'auth.alreadyHaveAccount': 'Pehle se account hai?',
  'auth.signInLink': 'Sign in karo',
  'auth.registrationFailed': 'Registration fail ho gaya. Phir try karo.'
};

const guLatn: Dict = {
  'install.cardTitle': 'Accountly install karo',
  'install.cardSub': 'Jaldi khule chhe ane offline pan chale chhe',
  'install.action': 'Install',
  'install.samsungTitle': 'Chrome thi install karo',
  'install.samsungStep1': 'Aa link copy karine Chrome ma kholo.',
  'install.samsungStep2': 'Chrome ma upar jamni baju ⋮ menu dabavo.',
  'install.samsungStep3': '"Install app" dabavo ane confirm karo.',
  'install.copyLink': 'Link copy karo',
  'install.copied': 'Copy thai gayu',
  'install.button': 'App install karo',
  'install.desc': 'Accountly ne tamari home screen par umero',
  'install.iosTitle': 'Home Screen par umero',
  'install.step1': 'Tamara browser ni neeche Share button dabavo.',
  'install.step2': 'Neeche scroll karo ane "Add to Home Screen" dabavo.',
  'install.step3': '"Add" dabavo. Have Accountly app ni jem khulshe.',
  'install.gotIt': 'Samji gayu',
  'notifications.title': 'Suchnao',
  'notifications.emptyTitle': 'Badhu update chhe',
  'notifications.emptySub': 'Tamari chukvani ane account sambandhit update ahi dekhase.',
  'due.add': 'Tarikh',
  'due.setDate': 'Tarikh nakki karo',
  'due.title': 'Baaki',
  'due.today': 'Aaje baaki',
  'due.tomorrowLabel': 'Kal baaki',
  'due.inDays': (v) => `${v.days} divas ma baaki`,
  'due.overdueBy': (v) => `${v.days} divas modu`,
  'due.failed': 'Tarikh update thai shaki nathi',
  'common.save': 'Save karo',
  'common.cancel': 'Cancel karo',
  'common.close': 'Bandh karo',
  'common.delete': 'Kadhi nakho',
  'common.edit': 'Badlo',
  'common.add': 'Umero',
  'common.search': 'Shodho',
  'common.seeAll': 'Badhu joo',
  'common.saving': 'Save thai rahyu chhe…',
  'common.name': 'Naam',
  'common.phone': 'Phone',
  'common.balance': 'Baki rakam',
  'common.details': 'Vigato',
  'common.settings': 'Settings',
  'common.apply': 'Apply Karo',
  'common.reset': 'Reset Karo',

  'nav.home': 'Home',
  'nav.contacts': 'Contacts',
  'nav.payments': 'Vyavharo',
  'nav.more': 'Vadhu',

  'header.manageKhata': 'Khatu manage karo',
  'role.owner': 'Malik',
  'role.admin': 'Admin',
  'role.staff': 'Staff',
  'role.super_admin': 'Super Admin',

  'menu.profile': 'Profile',
  'menu.settings': 'Settings',
  'menu.logout': 'Logout',

  'home.youllGet': 'Tamne Malshe',
  'home.youllGive': 'Tamare Aapvana',
  'home.viewFullReport': 'Sampurna report joo',
  'home.addContact': 'Contact Umero',
  'home.newContact': 'Navo contact',
  'home.newEntry': 'Navo Transaction',
  'home.recordPayment': 'Chukvani nondho',
  'home.contacts': 'Taajetar na Contacts',
  'home.recentPayments': 'Taajetar na Vyavharo',
  'home.noContactsYet': 'Hju koi contact nathi',
  'home.noContactsSub': 'Kon ketlu aape chhe te jaanva mate tamaro pehlo contact umero.',
  'home.addContactCta': 'Contact umero',
  'home.noPaymentsYet': 'Hju koi chukvani nathi',
  'home.noPaymentsSub': 'Koi contact kholo ane Send ke Receive nondho, e ahi dekhashe.',
  'home.paidTo': (v) => `${v.name} ne udhar aapya`,
  'home.receivedFrom': (v) => `${v.name} pasethi udhar lidha`,

  'contacts.title': 'Contacts',
  'contactType.title': 'Type',
  'contactType.customer': 'Customer',
  'contactType.supplier': 'Supplier',
  'contactType.business': 'Business',
  'contactType.friend': 'Mitra',
  'contactType.family': 'Parivar',
  'contacts.searchPlaceholder': 'Naam ke phone thi shodho',
  'contacts.noMatches': 'Kai malyu nathi',
  'contacts.tryDifferent': 'Biju naam ke number try karo.',
  'contacts.tapAddToCreate': 'Pehlo contact banavva Add dabao.',

  'detail.youWillGet': 'Tamne Malshe',
  'detail.youWillGive': 'Tamare Aapvana',
  'detail.transactions': 'Vyavharo',
  'detail.recentTransactions': 'Taajetar na Vyavharo',
  'detail.youGave': 'Udhar Aapya',
  'detail.youGot': 'Udhar Lidha',
  'detail.noTransactionsYet': 'Hju koi vyavhar nathi',
  'detail.recordBelow': 'Niche Send ke Receive nondho.',
  'detail.shareLedger': 'Khatu Share Karo',
  'detail.failedGenerateLedger': 'Khatu banavva ma nishfal',
  'detail.youGaveBtn': 'Udhar Aapya',
  'detail.youGotBtn': 'Udhar Lidha',
  'detail.sendReminderWhatsapp': 'WhatsApp Reminder',
  'detail.sendReminderSms': 'SMS Reminder',
  'detail.reminderDue': (v) => `Hi ${v.name}, tamare ${v.business} ne ${v.amount} nu udhar aapvanu baki chhe. Krupa kari jaldi chukvo. Aabhar!`,
  'detail.reminderOwed': (v) => `Hi ${v.name}, ${v.business} taraf thi tamne ${v.amount} malvana baki chhe. Ame jaldi chukvi aapishu. Aabhar!`,

  'contactSettings.title': 'Contact Settings',
  'contactSettings.notFound': 'Contact malyo nathi.',
  'contactSettings.actions': 'Actions',
  'contactSettings.editContact': 'Contact Badlo',
  'contactSettings.editContactSub': 'Naam, phone ke address badlo',
  'contactSettings.deleteContact': 'Contact Kadhi Nakho',
  'contactSettings.deleteContactSub': 'Aa contact ne hammesh mate kadhi nakho',
  'contactSettings.deleteQ': 'Contact kadhi nakhvo chhe?',
  'contactSettings.deleteBody': (v) => `${v.name} ane temno vyavhar itihaas kadhi nakhashe. Aa pachu nahi thay.`,
  'contactSettings.failedDelete': 'Contact delete na thayo',

  'contactForm.contactName': 'Contact Nu Naam',
  'contactForm.enterName': 'Naam lakho',
  'contactForm.nameRequired': 'Naam jaruri chhe',
  'contactForm.phoneNumber': 'Phone Number',
  'contactForm.enterPhoneNumber': 'Phone number nakho',
  'contactForm.invalidPhone': 'Sachho phone number nakho',
  'contactForm.address': 'Sarnamu',
  'contactForm.optional': 'Optional',
  'contactForm.addTitle': 'Contact Umero',
  'contactForm.editTitle': 'Contact Badlo',
  'contactForm.saveChanges': 'Ferfaro Save Karo',
  'contactForm.businessInfoNotFound': 'Business ni mahiti nathi malti',
  'contactForm.failedAdd': 'Contact umervama nishfal',
  'contactForm.failedUpdate': 'Contact badalvama nishfal',
  'contactForm.failedImageUpload': 'Photo upload na thai',
  'contactForm.alreadyExists': 'Aa phone number thi contact pehle thi chhe',
  'contactForm.selectCountry': 'Desh Pasand Karo',
  'contactForm.searchCountries': 'Desh shodho',

  'payment.editEntry': 'Transaction Badlo',
  'payment.youGaveTitle': 'Udhar Aapya',
  'payment.youGotTitle': 'Udhar Lidha',
  'payment.to': 'ne',
  'payment.from': 'pasethi',
  'payment.addNote': 'Note umero (optional)',
  'payment.update': 'Update Karo',
  'payment.saveEntry': 'Transaction Save Karo',
  'payment.entrySaved': 'Transaction Save Thayo',
  'payment.contactNotFound': 'Contact malyo nathi',
  'payment.failedUpdateEntry': 'Transaction update na thayu',
  'payment.failedRecordEntry': 'Transaction nondhvama nishfal',
  'payment.failedDeleteEntry': 'Transaction kadhvama nishfal',
  'payment.deleteEntryQ': 'Aa transaction kadhvu chhe?',
  'payment.deleteEntryBody': 'Aanathi contact ni baki rakam badlashe. Aa pachu nahi thay.',
  'payment.date': 'Tarikh',
  'payment.attachBills': 'Photo Jodo',
  'payment.imageAttached': 'Photo jodayu',
  'payment.failedAttachImage': 'Photo jodva ma nishfad thayu',
  'payment.comingSoon': 'Bill jodvani suvidha tunk samay ma aavshe',
  'payment.tapToUse': 'Use karva tap karo',
  'transactionDetail.title': 'Vyavhar ni Vigato',
  'transactionDetail.note': 'Nondh',
  'transactionDetail.attachment': 'Attachment',
  'transactionDetail.viewAttachment': 'Attachment Juo',

  'transactions.title': 'Vyavharo',
  'transactions.searchPayments': 'Chukvani shodho',
  'transactions.openContactRecord': 'Koi contact kholo ane Send ke Receive nondho.',
  'transactions.filters': 'Filters',
  'transactions.dateRangeLabel': 'Tarikh Range',
  'transactions.typeLabel': 'Prakar',
  'transactions.filterToday': 'Aaje',
  'transactions.filterYesterday': 'Gaikale',
  'transactions.filterThisWeek': 'Aa Athvadiye',
  'transactions.filterThisMonth': 'Aa Mahine',
  'transactions.filterLastMonth': 'Gayo Mahino',
  'transactions.filterAllTime': 'Badho Time',
  'transactions.filterCustomRange': 'Custom Range',
  'transactions.filterAllTypes': 'Badha',
  'transactions.startDate': 'Sharuaat ni Tarikh',
  'transactions.endDate': 'Antim Tarikh',

  'expenses.title': 'Vyaktigat Kharch',
  'expenses.searchExpenses': 'Kharch shodho',
  'expenses.noExpensesYet': 'Hju koi kharch nathi',
  'expenses.noExpensesSub': 'Tamaro pehlo vyaktigat kharch umervaa mate Add dabao.',
  'expenses.filters': 'Filters',
  'expenses.categoryLabel': 'Category',
  'expenses.totalSpent': 'Kul kharch',
  'expenses.moreThan': (v) => `${v.period} karta ${v.amount} vadhu`,
  'expenses.lessThan': (v) => `${v.period} karta ${v.amount} ochhu`,
  'expenses.sameAs': (v) => `${v.period} jetlu j`,
  'expenses.cmpYesterday': 'gaikaal',
  'expenses.cmpDayBefore': 'param divas',
  'expenses.cmpLastWeek': 'gaya athvadiye aa samay',
  'expenses.cmpLastMonth': 'gaya mahine aa samay',
  'expenses.cmpMonthBefore': 'teni pehlano mahino',
  'expenses.cmpPreviousDays': (v) => `pachhla ${v.days} divas`,

  'expense.category.food': 'Khoraak',
  'expense.category.travel': 'Musafari',
  'expense.category.rent': 'Bhadu',
  'expense.category.utilities': 'Vijadi-Pani',
  'expense.category.shopping': 'Kharidi',
  'expense.category.other': 'Anya',

  'expense.newEntry': 'Navo Kharch',
  'expense.editEntry': 'Kharch Badlo',
  'expense.addNote': 'Nondh umero (optional)',
  'expense.saveEntry': 'Kharch Save Karo',
  'expense.update': 'Update Karo',
  'expense.failedRecordEntry': 'Kharch nondhvama nishfal',
  'expense.failedUpdateEntry': 'Kharch update karvama nishfal',
  'expense.failedDeleteEntry': 'Kharch delete karvama nishfal',
  'expense.deleteEntryQ': 'Aa kharch delete karvo chhe?',
  'expense.deleteEntryBody': 'Aa pachu nahi thay.',

  'expenseDetail.title': 'Kharch ni Vigato',
  'expenseDetail.note': 'Nondh',

  'more.title': 'Vadhu',
  'more.profileDesc': 'Tamaru naam ane phone',
  'more.settingsDesc': 'Theme ane language',
  'more.personalExpenses': 'Vyaktigat Kharch',
  'more.personalExpensesDesc': 'Tamaro daily kharch track karo',
  'more.reports': 'Reports',
  'more.reportsDesc': 'Kharch ane business ni mahiti juo',
  'more.notes': 'Notes',
  'more.notesDesc': 'Jaldi notes lakho',
  'more.reminders': 'Reminders',
  'more.remindersDesc': 'Sacha samay par yaad apavo',

  'notes.title': 'Notes',
  'notes.searchNotes': 'Notes shodho',
  'notes.noNotesYet': 'Hju koi note nathi',
  'notes.noNotesSub': 'Tamaro pehlo note lakhva mate Add dabao.',

  'note.placeholder': 'Lakhvanu shuru karo...',
  'note.saving': 'Save thai rahyu chhe…',
  'note.saved': 'Save thai gayu',
  'note.deleteNoteQ': 'Aa note delete karvi chhe?',
  'note.deleteNoteBody': 'Aa pachu nahi thay.',
  'note.failedSave': 'Note save karvama nishfal',
  'note.failedDelete': 'Note delete karvama nishfal',

  'reminders.title': 'Reminders',
  'reminders.new': 'Navu reminder',
  'reminders.reminder': 'Reminder',
  'reminders.upcoming': 'Aavnara',
  'reminders.past': 'Pachhla',
  'reminders.today': 'Aaje',
  'reminders.tomorrow': 'Aavtikale',
  'reminders.yesterday': 'Gaikale',
  'reminders.later': 'Pachhi',
  'reminders.emptyUpcoming': 'Haji koi reminder nathi',
  'reminders.emptyUpcomingSub': 'Add dabavo, ame sacha samay par yaad apavishu.',
  'reminders.emptyPast': 'Ahiya haji kai nathi',
  'reminders.emptyPastSub': 'Je reminders vaagi gaya chhe te ahiya dekhashe.',
  'reminders.titlePlaceholder': 'Shu yaad apavvu chhe?',
  'reminders.notesPlaceholder': 'Vigato umero (optional)',
  'reminders.when': 'Kyare',
  'reminders.repeat': 'Repeat',
  'reminders.repeat.none': 'Ek vaar',
  'reminders.repeat.daily': 'Darroj',
  'reminders.repeat.weekly': 'Dar athvadiye',
  'reminders.repeat.monthly': 'Dar mahine',
  'reminders.early': 'Vahelu reminder',
  'reminders.early.none': 'Koi nahi',
  'reminders.early.5': '5 min',
  'reminders.early.15': '15 min',
  'reminders.early.30': '30 min',
  'reminders.early.60': '1 kalak',
  'reminders.early.1440': '1 divas',
  'reminders.time': 'Samay',
  'reminders.date': 'Tarikh',
  'reminders.save': 'Reminder save karo',
  'reminders.remindedAt': (v) => `${v.when} yaad apavyu`,
  'reminders.snooze': 'Snooze',
  'reminders.snooze.10': '10 min',
  'reminders.snooze.60': '1 kalak',
  'reminders.snooze.1440': 'Kale',
  'reminders.done': 'Thai gayu',
  'reminders.markDone': 'Done mark karo',
  'reminders.markDoneSub': 'Raah joya vina Pachhla ma khasedo',
  'reminders.state.fired': 'Yaad apavyu',
  'reminders.state.done': 'Thai gayu',
  'reminders.deleteQ': 'Aa reminder kadhi nakhvu chhe?',
  'reminders.deleteBody': 'Aa paachhu nahi aavi shake.',
  'reminders.failedSave': 'Reminder save na thai shakyu',
  'reminders.failedUpdate': 'Reminder update na thai shakyu',
  'reminders.failedDelete': 'Reminder kadhi na shakayu',
  'reminders.notFound': 'Aa reminder have nathi.',
  'reminders.needTitle': 'Shu yaad apavvu chhe te lakho.',
  'push.title': 'Reminder alerts',
  'push.offBody': 'Notifications chalu karo jethi Accountly bandh hoy tyare pan reminder male.',
  'push.onBody': 'Aa device par reminder alerts malshe.',
  'push.installBody': 'iPhone par reminder alerts mate Accountly ne Home Screen par umero.',
  'push.deniedBody': 'Notifications block chhe. Browser settings ma aa site mate allow karo.',
  'push.unsupportedBody': 'Aa browser notifications batavi shaktu nathi.',
  'push.failed': 'Notifications chalu na thai shakya. Fari try karo.',

  'profile.title': 'Profile',
  'profile.businessName': 'Business Nu Naam',
  'profile.updated': 'Profile update thai',
  'profile.failedUpdate': 'Profile update na thai',
  'profile.failedAvatarUpload': 'Photo upload na thai',
  'profile.failedLogoUpload': 'Logo upload na thai',
  'profile.businessLogo': 'Business Logo',
  'profile.businessLogoSub': 'Bills ane shared ledger ma dekhashe',

  'settings.title': 'Settings',
  'settings.preferences': 'Pasandagio',
  'settings.darkMode': 'Dark Mode',
  'settings.darkModeSub': 'Ghatta rang ni theme vaparo',
  'settings.language': 'Language',
  'settings.currency': 'Currency',
  'settings.themeSaveFail': 'Theme save na thai',
  'settings.languageSaveFail': 'Language save na thai',
  'settings.currencySaveFail': 'Currency save na thai. Bani shake tamne badalvani permission nathi.',

  'reports.title': 'Reports',
  'reports.filters': 'Filters',
  'reports.collected': 'Malyu',
  'reports.given': 'Aapyu',
  'reports.net': 'Net',
  'reports.trend': 'Malyu vs Aapyu',
  'reports.topContacts': 'Top Contacts',
  'reports.noDataYet': 'Aa samay ma koi transaction nathi',
  'reports.noDataSub': 'Alag date range try karo.',

  'ledger.statementTitle': 'Khata Statement',
  'ledger.generatedOn': 'Banavyu tareek',
  'ledger.currentBalance': 'Current Balance',
  'ledger.date': 'Date',
  'ledger.description': 'Description',
  'ledger.debit': 'Debit',
  'ledger.credit': 'Credit',
  'ledger.balance': 'Balance',
  'ledger.footer': 'Accountly dwara banavelu',
  'ledger.billTo': 'Bill To',
  'ledger.page': 'Page',
  'ledger.of': 'of',
  'ledger.amountDue': 'Amount Due',
  'ledger.creditBalance': 'Credit Balance',
  'ledger.givenTo': (v) => `${v.name} ne aapyu`,
  'ledger.receivedFrom': (v) => `${v.name} pasethi malyu`,

  'auth.welcomeBack': 'Pacha aavva badal aavkar',
  'auth.signInToKhata': 'Tamara khata ma sign in karo',
  'auth.enterPhoneNumber': 'Phone number nakho',
  'auth.password': 'Password',
  'auth.signIn': 'Sign In',
  'auth.newToAccountly': 'Accountly par nava chho?',
  'auth.createAccount': 'Account banavo',
  'auth.loginFailed': 'Login fail thayu. Fari try karo.',
  'auth.createYourAccount': 'Tamaru account banavo',
  'auth.startManaging': 'Minitoma tamaru khatu manage karvanu shuru karo',
  'auth.businessName': 'Business nu naam',
  'auth.yourName': 'Tamaru naam',
  'auth.createAccountBtn': 'Account Banavo',
  'auth.creatingAccount': 'Account bani rahyu chhe…',
  'auth.signingIn': 'Sign in thai rahyu chhe…',
  'auth.alreadyHaveAccount': 'Pehle thi account chhe?',
  'auth.signInLink': 'Sign in karo',
  'auth.registrationFailed': 'Registration fail thayu. Fari try karo.'
};

const dicts: Record<I18n, Dict> = { en, hi, gu, 'hi-latn': hiLatn, 'gu-latn': guLatn };

export const translate = (locale: I18n, key: string, vars?: Vars): string => {
  const entry = dicts[locale]?.[key] ?? dicts.en[key];
  if (entry == null) return key;
  return typeof entry === 'function' ? entry(vars || {}) : entry;
};

export const useT = () => {
  const { language } = useConfig();
  const locale = (language as I18n) || 'en';
  return useCallback((key: string, vars?: Vars) => translate(locale, key, vars), [locale]);
};
